import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  PlusIcon,
  ShieldCheckIcon,
  StarIcon,
  CurrencyDollarIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  ArrowPathIcon,
  ArrowUpCircleIcon,
  ArrowTopRightOnSquareIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CubeIcon,
  Square3Stack3DIcon,
  ExclamationCircleIcon,
  TrophyIcon,
  PuzzlePieceIcon,
  FunnelIcon,
  InformationCircleIcon,
  ChartBarIcon,
  NoSymbolIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { getSelfUsername } from '../services/userSelf';
import { getUsersData, buildTree } from '../data/users';
import useTranslation from '../services/translationService';
import { fetchAuthSession } from '@aws-amplify/auth';
import Loading from '../components/Loading';
import { formatCurrency } from '../utils/currency';
import { getGameNameWithFallback, gameNameMapping } from '../utils/gameNameMapping';
import { useNotification } from '../components/NotificationContext';
import { useConfirm } from '../components/ConfirmDialog';
import { 
  fetchBetLimits, 
  saveBetLimit, 
  saveBulkBetLimits, 
  deleteBetLimit 
} from '../services/limitsService';
import {
  fetchCustomSportsEvents,
  createCustomSportsEvent,
  importCustomSportsEvents,
  deleteCustomSportsEvent
} from '../services/sportsCatalogService';
import { isBreakglassModeActive } from '../services/breakglassService';

// The history tab lists stakes the casino refused for hitting a limit. Acting
// on one reuses the same editor a bet opens, so a refusal and a bet lead to the
// same place: pick the scope, pick the account, set the amount.
import LimitHistory from '../components/LimitHistory';
import LimitScopeModal from '../components/LimitScopeModal';
import {
  buildBetFromEvent,
  buildLimitSuggestion,
  getPreferredScopeKey,
  getBreachMeta,
  formatDateTime
} from '../utils/limitEvents';

// Catalogs, scope helpers and user-type helpers are shared with the quick-limit
// panel that opens from a bet, so both entry points write identical limit records.
import {
  GAME_PROVIDERS,
  SPORTS_ICONS,
  getAllSportsEvents,
  sportsEventMatchesQuery,
  getGameProvider,
  getTypeIcon,
  getTypeColor,
  getTypeLabel,
  isBlockingLimit
} from '../utils/limitScopes';

// Helper function to flatten the user tree into a searchable list
const flattenUserTree = (node, parentPath = []) => {
  const results = [];
  
  const processNode = (currentNode, currentPath) => {
    if (!currentNode) return;
    
    const nodePath = [...currentPath, currentNode.username];
    
    // Add ALL user types - anyone can have bet limits now
    if (currentNode.username) {
      results.push({
        ...currentNode,
        username: currentNode.username,
        name: currentNode.name || currentNode.username,
        type: currentNode.type || 'player',
        balances: currentNode.balances || {},
        attributes: currentNode.attributes || {},
        path: nodePath,
        parentPath: currentPath
      });
    }
    
    // Process children
    if (currentNode.children && currentNode.children.length > 0) {
      currentNode.children.forEach(child => {
        processNode(child, nodePath);
      });
    }
  };
  
  processNode(node, parentPath);
  console.log('flattenUserTree - Total users found:', results.length);
  return results;
};

// Note: API functions are imported from '../services/limitsService'
// - fetchBetLimits(username)
// - saveBetLimit(username, limit, userType)
// - saveBulkBetLimits(username, limits, userType) 
// - deleteBetLimit(username, limitId)

// ---------------------------------------------------------------------------
// Reading a stored limit
//
// A limit arrives as a flat record and there is one record per currency, so a
// raw list is long and repeats itself. Everything below turns that list into
// groups an admin can collapse: bucket -> scope -> one row per currency.
// ---------------------------------------------------------------------------

// Widest scope first: the rules that override everything else are the ones an
// admin needs to see before the narrow per-game ones.
const LIMIT_GROUPS = ['universal', 'sports', 'provider', 'game'];

const GROUP_META = {
  universal: {
    Icon: ExclamationCircleIcon,
    iconColor: 'text-red-600 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
    header: 'bg-red-50 dark:bg-red-900/20',
    chip: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
  },
  sports: {
    Icon: TrophyIcon,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800',
    header: 'bg-emerald-50 dark:bg-emerald-900/20',
    chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
  },
  provider: {
    Icon: Square3Stack3DIcon,
    iconColor: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-200 dark:border-purple-800',
    header: 'bg-purple-50 dark:bg-purple-900/20',
    chip: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
  },
  game: {
    Icon: CubeIcon,
    iconColor: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-200 dark:border-blue-800',
    header: 'bg-blue-50 dark:bg-blue-900/20',
    chip: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
  }
};

// Chips over the list. A bucket only earns a chip when the account actually has
// one, so the row stays thumb-width on a phone instead of scrolling.
const LIMIT_FILTERS = [
  { id: 'all', labelKey: 'limits.all', labelDefault: 'All', Icon: FunnelIcon, active: 'bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900' },
  { id: 'universal', labelKey: 'limits.group_universal', labelDefault: 'Everything', Icon: ExclamationCircleIcon, active: 'bg-red-600 text-white' },
  { id: 'sports', labelKey: 'limits.sports', labelDefault: 'Sports', Icon: TrophyIcon, active: 'bg-emerald-600 text-white' },
  { id: 'provider', labelKey: 'limits.providers', labelDefault: 'Providers', Icon: Square3Stack3DIcon, active: 'bg-purple-600 text-white' },
  { id: 'game', labelKey: 'limits.games', labelDefault: 'Games', Icon: CubeIcon, active: 'bg-blue-600 text-white' },
  { id: 'blocked', labelKey: 'limits.blocked', labelDefault: 'Blocked', Icon: NoSymbolIcon, active: 'bg-red-600 text-white' }
];

const getLimitGroup = (limit) => {
  const identifier = String(limit?.identifier || '');

  if (limit?.limitType === 'universal' || limit?.gameId === 'all' || identifier === 'all') {
    return 'universal';
  }
  if (
    limit?.limitType === 'sports' ||
    limit?.limitType === 'sports-event' ||
    limit?.gameId === 'sports' ||
    identifier === 'sports' ||
    identifier.startsWith('sports:')
  ) {
    return 'sports';
  }
  if (limit?.limitType === 'provider') {
    return 'provider';
  }
  return 'game';
};

/** Same rule in another currency has to land on the same card. */
const getLimitScopeKey = (limit) =>
  `${getLimitGroup(limit)}|${limit?.identifier || limit?.gameId || limit?.providerId || 'unknown'}`;

/**
 * Headline for what a limit covers, plus the catalog emoji when there is one.
 * Used for the selected user's own limits and for the inherited ones, so the
 * same rule reads the same wherever it shows up.
 */
const describeLimitScope = (limit, { t, customSportsEvents, customCasinoGames }) => {
  const group = getLimitGroup(limit);
  const identifier = limit?.identifier || limit?.gameId || limit?.providerId || '';
  const allVariants = t('limits.allVariants', 'All Variants');

  if (group === 'universal') {
    return { group, name: t('limits.allGamesProviders', 'ALL GAMES/PROVIDERS'), emoji: null };
  }

  if (group === 'sports') {
    if (limit?.limitType !== 'sports-event') {
      return { group, name: t('limits.allSports', 'ALL SPORTS'), emoji: null };
    }
    const eventId = limit.sportsEventId || String(identifier).replace('sports:', '');
    const event = getAllSportsEvents(customSportsEvents).find((e) => e.id === eventId);
    return {
      group,
      name: limit.sportsEventName || event?.name || eventId,
      emoji: event?.icon || '🏆'
    };
  }

  if (group === 'provider') {
    const provider = GAME_PROVIDERS.find((p) => p.id === identifier);
    return { group, name: provider?.name || identifier, emoji: null };
  }

  const customGame = customCasinoGames.find((g) => g.id === identifier);
  if (customGame) {
    return {
      group,
      name: customGame.isGeneral ? `${customGame.name} (${allVariants})` : customGame.name,
      emoji: customGame.icon || null
    };
  }

  const generalNames = {
    blackjack: t('limits.generalBlackjack', 'Blackjack (All Variants)'),
    roulette: t('limits.generalRoulette', 'Roulette (All Variants)'),
    baccarat: t('limits.generalBaccarat', 'Baccarat (All Variants)')
  };
  if (generalNames[identifier]) {
    return { group, name: generalNames[identifier], emoji: null };
  }

  if (!identifier) {
    return { group, name: t('limits.unknownLimit', 'Unknown Limit'), emoji: null };
  }

  const isAllVariants = limit?.limitType === 'game-general';
  const knownName = getGameNameWithFallback(identifier, '');
  if (knownName) {
    return { group, name: isAllVariants ? `${knownName} (${allVariants})` : knownName, emoji: null };
  }

  return {
    group,
    name: isAllVariants
      ? `${t('limits.customGameType', 'Custom Game Type')}: ${identifier} (${allVariants})`
      : `${t('limits.customGame', 'Custom Game')} (${identifier})`,
    emoji: null
  };
};

/** Emoji when the catalog has one, otherwise the icon that stands for the bucket. */
const ScopeGlyph = ({ scope, className = 'w-4 h-4' }) => {
  if (scope.emoji) {
    return <span className="text-base leading-none shrink-0">{scope.emoji}</span>;
  }
  const { Icon, iconColor } = GROUP_META[scope.group] || GROUP_META.game;
  return <Icon className={`${className} ${iconColor} shrink-0`} />;
};

const Limits = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  // Not window.confirm: the panel runs as an installed PWA, where a native
  // confirm can silently return false and leave the button looking dead.
  const confirm = useConfirm();
  
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTree, setUserTree] = useState(null);
  const [flatUsers, setFlatUsers] = useState([]);
  const [betLimits, setBetLimits] = useState([]);
  const [isLoadingLimits, setIsLoadingLimits] = useState(false);
  const [editingLimit, setEditingLimit] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    inherited: true,
    info: false
  });
  // Explicit open/closed state per bucket and per ancestor. Anything missing
  // falls back to a sensible default, so a fresh user starts readable.
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedParents, setExpandedParents] = useState({});
  const [limitSearch, setLimitSearch] = useState('');
  const [isSavingLimit, setIsSavingLimit] = useState(false);
  
  // Form state for new/edit limit
  const [limitForm, setLimitForm] = useState({
    type: '', // 'sports' or 'casino'
    sportsEventIds: [],  // One limit is written per selected event
    gameIds: [],  // Changed to array for multiple games
    providerIds: [],  // Added for provider-wide limits
    currency: '',
    maxBet: '',
    minBet: ''  // NEW: Minimum bet
  });
  const [gameSearchTerm, setGameSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // NEW: Filter limits by type
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false); // NEW: Toggle advanced options
  
  // Modal states for provider and game selection
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [showSportsEventModal, setShowSportsEventModal] = useState(false);
  const [tempProviderSelection, setTempProviderSelection] = useState([]);
  const [tempGameSelection, setTempGameSelection] = useState([]);
  const [sportsEventSearchTerm, setSportsEventSearchTerm] = useState('');
  const [tempSportsSelection, setTempSportsSelection] = useState([]);
  
  // Custom sports event state
  const [showAddCustomEvent, setShowAddCustomEvent] = useState(false);
  const [customEventForm, setCustomEventForm] = useState({
    id: '',
    name: '',
    category: 'event',
    icon: '🏆',
    aliases: ''
  });
  // Loaded from the shared catalog, not localStorage: a keyword one admin adds
  // has to be visible to the others and to the AI assistant.
  const [customSportsEvents, setCustomSportsEvents] = useState([]);
  const [isSavingEvent, setIsSavingEvent] = useState(false);
  
  // Custom casino game state
  const [showAddCustomGame, setShowAddCustomGame] = useState(false);
  const [customGameForm, setCustomGameForm] = useState({
    id: '',
    name: '',
    isGeneral: true,
    icon: '🎰'
  });
  const [customCasinoGames, setCustomCasinoGames] = useState(() => {
    // Load from localStorage on mount
    const saved = localStorage.getItem('customCasinoGames');
    return saved ? JSON.parse(saved) : [];
  });
  
  // Current user info
  const [currentUserType, setCurrentUserType] = useState(null);
  const [selfUsername, setSelfUsername] = useState('');
  const [isBreakglassActive, setIsBreakglassActive] = useState(false);
  
  // NEW: Copy/Template functionality
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copySourceUser, setCopySourceUser] = useState(null);
  
  // NEW: Parent limits (hierarchy)
  const [parentLimits, setParentLimits] = useState([]);

  // Limits are what is configured; history is what those limits actually did.
  const [activeTab, setActiveTab] = useState('limits');
  // Set when an admin acts on a refusal, and read by the limit editor to
  // preselect the scope, the account and the amount that would have passed.
  const [limitFromEvent, setLimitFromEvent] = useState(null);

  // Check for breakglass mode
  useEffect(() => {
    const checkBreakglass = async () => {
      const breakglassActive = await isBreakglassModeActive();
      setIsBreakglassActive(breakglassActive);
    };
    checkBreakglass();
  }, []);

  // Load the shared sports catalog, migrating anything this browser was still
  // holding on to from when custom events lived in localStorage.
  useEffect(() => {
    const loadCatalog = async () => {
      const shared = await fetchCustomSportsEvents();

      let stranded = [];
      try {
        stranded = JSON.parse(localStorage.getItem('customSportsEvents') || '[]');
      } catch {
        stranded = [];
      }

      const missing = stranded.filter((local) => !shared.some((event) => event.id === local.id));
      if (missing.length === 0) {
        setCustomSportsEvents(shared);
        if (stranded.length > 0) localStorage.removeItem('customSportsEvents');
        return;
      }

      const result = await importCustomSportsEvents(missing);
      if (result.success) {
        setCustomSportsEvents(result.events);
        localStorage.removeItem('customSportsEvents');
        showNotification(
          'success',
          t('common.success'),
          t('limits.eventsMigrated', `${missing.length} custom event(s) from this device are now shared with all admins`)
        );
      } else {
        // Keep them on screen and in localStorage so nothing is lost
        setCustomSportsEvents([...shared, ...missing]);
      }
    };

    loadCatalog();
  }, [showNotification, t]);

  // Fetch user tree on mount
  useEffect(() => {
    const loadUserData = async () => {
      try {
        setIsLoading(true);
        
        // Get current user info
        const { tokens } = await fetchAuthSession();
        const username = await getSelfUsername();
        setSelfUsername(username);
        
        // Fetch users data and build tree (same approach as AgentTree)
        const response = await getUsersData();
        console.log('Limits page - Users data loaded:', { success: response.success, hasData: !!response.data });
        
        if (response.success && response.data) {
          // Build the tree from the data
          const tree = await buildTree(response.data, response.data.rootId);
          console.log('Limits page - Tree built:', { treeExists: !!tree, userType: tree?.type });
          
          if (tree) {
            setUserTree(tree);
            setCurrentUserType(tree.type);
            
            // Flatten the tree for searching
            const flattened = flattenUserTree(tree);
            console.log('Limits page - Flattened users:', flattened.length, 'users found');
            console.log('Sample flattened users:', flattened.slice(0, 3));
            setFlatUsers(flattened);
          }
        } else {
          console.error('Limits page - Failed to load users data');
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        showNotification('error', t('common.error'), t('limits.errorLoadingUsers'));
      } finally {
        setIsLoading(false);
      }
    };
    
    loadUserData();
  }, [showNotification, t]);

  // Load bet limits when user is selected
  useEffect(() => {
    if (selectedUser) {
      console.log('Selected user changed, loading limits for:', selectedUser.username);
      loadBetLimits();
      loadParentLimits(); // NEW: Load parent hierarchy limits
    }
  }, [selectedUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // A filter or a collapsed bucket that made sense for the previous account
  // would silently hide the new one's limits, so start every account clean.
  useEffect(() => {
    setExpandedGroups({});
    setExpandedParents({});
    setLimitSearch('');
    setFilterType('all');
  }, [selectedUser?.username]);

  // Load bet limits function
  const loadBetLimits = async () => {
    if (!selectedUser) return;
    
    console.log('Loading bet limits for user:', selectedUser.username);
    setIsLoadingLimits(true);
    try {
      const result = await fetchBetLimits(selectedUser.username);
      console.log('Bet limits result:', result);
      
      if (result.success) {
        setBetLimits(result.limits || []);
        console.log('Set bet limits:', result.limits);
      } else {
        console.error('Failed to load limits:', result.error);
        showNotification('warning', t('common.warning'), result.error || t('limits.errorLoadingLimits'));
        setBetLimits([]);
      }
    } catch (error) {
      console.error('Error loading bet limits:', error);
      showNotification('error', t('common.error'), t('limits.errorLoadingLimits'));
      setBetLimits([]);
    } finally {
      setIsLoadingLimits(false);
    }
  };

  // NEW: Load parent hierarchy limits
  const loadParentLimits = async () => {
    if (!selectedUser || !selectedUser.parentPath || selectedUser.parentPath.length === 0) {
      setParentLimits([]);
      return;
    }
    
    console.log('Loading parent limits for hierarchy:', selectedUser.parentPath);
    
    try {
      // Fetch limits for each parent in the hierarchy
      const parentLimitsPromises = selectedUser.parentPath.map(async (parentUsername) => {
        try {
          const result = await fetchBetLimits(parentUsername);
          if (result.success && result.limits && result.limits.length > 0) {
            return {
              username: parentUsername,
              limits: result.limits
            };
          }
        } catch (error) {
          console.error(`Error loading limits for parent ${parentUsername}:`, error);
        }
        return null;
      });
      
      const resolvedParentLimits = await Promise.all(parentLimitsPromises);
      const validParentLimits = resolvedParentLimits.filter(p => p !== null);
      
      console.log('Loaded parent limits:', validParentLimits);
      setParentLimits(validParentLimits);
    } catch (error) {
      console.error('Error loading parent limits:', error);
      setParentLimits([]);
    }
  };

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return [];
    
    const term = searchTerm.toLowerCase();
    const filtered = flatUsers.filter(user => 
      user.username.toLowerCase().includes(term) ||
      user.name?.toLowerCase().includes(term) ||
      user.type.toLowerCase().includes(term)
    ).slice(0, 50); // Limit results for performance
    
    console.log('Limits page - Search:', { 
      searchTerm, 
      flatUsersCount: flatUsers.length, 
      filteredCount: filtered.length 
    });
    
    return filtered;
  }, [flatUsers, searchTerm]);

  // -------------------------------------------------------------------------
  // Turning flat limit lists into something readable on a phone
  // -------------------------------------------------------------------------

  const describeLimit = useCallback(
    (limit) => describeLimitScope(limit, { t, customSportsEvents, customCasinoGames }),
    [t, customSportsEvents, customCasinoGames]
  );

  /** bucket -> scope -> one entry per currency, each level sorted for scanning. */
  const groupLimits = useCallback(
    (limits) => {
      const buckets = new Map();

      limits.forEach((limit) => {
        const info = describeLimit(limit);
        if (!buckets.has(info.group)) buckets.set(info.group, new Map());

        const scopes = buckets.get(info.group);
        const key = getLimitScopeKey(limit);
        if (!scopes.has(key)) {
          scopes.set(key, { key, name: info.name, emoji: info.emoji, group: info.group, limits: [] });
        }
        scopes.get(key).limits.push(limit);
      });

      return LIMIT_GROUPS.filter((id) => buckets.has(id)).map((id) => {
        const scopes = Array.from(buckets.get(id).values())
          .map((scope) => ({
            ...scope,
            limits: [...scope.limits].sort((a, b) =>
              String(a.currency || '').localeCompare(String(b.currency || ''))
            )
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        const all = scopes.flatMap((scope) => scope.limits);
        return {
          id,
          scopes,
          limitCount: all.length,
          blockedCount: all.filter(isBlockingLimit).length
        };
      });
    },
    [describeLimit]
  );

  const limitCounts = useMemo(() => {
    const counts = { all: betLimits.length, universal: 0, sports: 0, provider: 0, game: 0, blocked: 0 };
    betLimits.forEach((limit) => {
      counts[getLimitGroup(limit)] += 1;
      if (isBlockingLimit(limit)) counts.blocked += 1;
    });
    return counts;
  }, [betLimits]);

  const visibleLimits = useMemo(() => {
    const term = limitSearch.trim().toLowerCase();

    return betLimits.filter((limit) => {
      const matchesFilter =
        filterType === 'all' ||
        (filterType === 'blocked' ? isBlockingLimit(limit) : filterType === getLimitGroup(limit));
      if (!matchesFilter) return false;
      if (!term) return true;

      return (
        describeLimit(limit).name.toLowerCase().includes(term) ||
        String(limit.currency || '').toLowerCase().includes(term)
      );
    });
  }, [betLimits, filterType, limitSearch, describeLimit]);

  const limitGroups = useMemo(() => groupLimits(visibleLimits), [groupLimits, visibleLimits]);

  // Ancestors that actually carry a limit, closest first: the nearest one is
  // what an admin reaches for, and it is the one their own limits argue with.
  const inheritedGroups = useMemo(
    () =>
      parentLimits
        .map((parent) => {
          const node = flatUsers.find((user) => user.username === parent.username);
          return {
            username: parent.username,
            name: node?.name || parent.username,
            type: node?.type || 'agent',
            limitCount: parent.limits.length,
            blockedCount: parent.limits.filter(isBlockingLimit).length,
            groups: groupLimits(parent.limits)
          };
        })
        .reverse(),
    [parentLimits, flatUsers, groupLimits]
  );

  const inheritedCount = useMemo(
    () => inheritedGroups.reduce((sum, parent) => sum + parent.limitCount, 0),
    [inheritedGroups]
  );

  // Short lists read fine wide open; long ones are the reason this page needed
  // grouping, so those start closed and show their counts instead.
  const autoExpandGroups = visibleLimits.length <= 8;
  const isGroupExpanded = (groupId) => expandedGroups[groupId] ?? autoExpandGroups;
  const areAllGroupsExpanded = limitGroups.length > 0 && limitGroups.every((group) => isGroupExpanded(group.id));

  const toggleGroup = (groupId) =>
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !(prev[groupId] ?? autoExpandGroups) }));

  const setAllGroupsExpanded = (expanded) =>
    setExpandedGroups(
      limitGroups.reduce((acc, group) => {
        acc[group.id] = expanded;
        return acc;
      }, {})
    );

  const isParentExpanded = (username, index) => expandedParents[username] ?? index === 0;

  const toggleParent = (username, index) =>
    setExpandedParents((prev) => ({ ...prev, [username]: !(prev[username] ?? index === 0) }));

  // Get available currencies from selected user's balances
  const availableCurrencies = useMemo(() => {
    if (!selectedUser) return [];
    const currencies = Object.keys(selectedUser.balances || {});
    // If no currencies, add default ones
    if (currencies.length === 0) {
      return ['USD', 'EUR', 'GBP']; // Default currencies
    }
    return currencies;
  }, [selectedUser]);

  // Get game options - combine regular games with special blackjack/roulette options
  const gameOptions = useMemo(() => {
    const generalOptions = [
      { id: 'blackjack', name: t('limits.generalBlackjack'), isGeneral: true, provider: 'all' },
      { id: 'roulette', name: t('limits.generalRoulette'), isGeneral: true, provider: 'all' },
      { id: 'baccarat', name: t('limits.generalBaccarat', 'Baccarat (All Variants)'), isGeneral: true, provider: 'all' }
    ];
    
    // Add custom casino games
    const customGames = customCasinoGames.map(game => ({
      id: game.id,
      name: game.name,
      isGeneral: game.isGeneral,
      provider: 'custom',
      custom: true,
      icon: game.icon
    }));
    
    // Add all specific games from mapping with provider info
    const specificGames = Object.entries(gameNameMapping).map(([id, name]) => ({
      id,
      name,
      isGeneral: false,
      provider: getGameProvider(id, name)
    }));
    
    // Combine: general options + custom games + specific games
    return [...generalOptions, ...customGames, ...specificGames];
  }, [t, customCasinoGames]);

  // Filter games based on search term
  const filteredGames = useMemo(() => {
    if (!gameSearchTerm.trim()) {
      // Show general options, custom games, and first 10 specific games when no search
      const generalGames = gameOptions.filter(g => g.isGeneral && !g.custom);
      const customGames = gameOptions.filter(g => g.custom);
      const specificGames = gameOptions.filter(g => !g.isGeneral && !g.custom).slice(0, 10);
      return [...generalGames, ...customGames, ...specificGames];
    }
    
    const term = gameSearchTerm.toLowerCase();
    const filtered = gameOptions.filter(game => 
      game.name.toLowerCase().includes(term) ||
      game.id.toLowerCase().includes(term) ||
      (game.provider && game.provider.toLowerCase().includes(term))
    );
    
    // Always keep general options at top if they match
    const generalMatches = filtered.filter(g => g.isGeneral && !g.custom);
    const customMatches = filtered.filter(g => g.custom);
    const specificMatches = filtered.filter(g => !g.isGeneral && !g.custom).slice(0, 50);
    
    return [...generalMatches, ...customMatches, ...specificMatches];
  }, [gameOptions, gameSearchTerm]);

  // What the sports picker is showing right now. "league" is a category word,
  // so it returns every league rather than only names that contain "league".
  const visibleSportsEvents = useMemo(() => (
    getAllSportsEvents(customSportsEvents).filter((event) =>
      event.id !== 'all-sports' && sportsEventMatchesQuery(event, sportsEventSearchTerm)
    )
  ), [customSportsEvents, sportsEventSearchTerm]);

  const fillCount = (text, count) => String(text).replace('{{count}}', String(count));

  const toggleSportsEvent = (eventId) => {
    setTempSportsSelection((prev) => {
      if (eventId === 'all-sports') {
        return prev.includes('all-sports') ? [] : ['all-sports'];
      }
      const withoutAll = prev.filter((id) => id !== 'all-sports');
      // Editing rewrites one record, so the picker stays a single choice.
      if (editingLimit) {
        return withoutAll.includes(eventId) ? [] : [eventId];
      }
      return withoutAll.includes(eventId)
        ? withoutAll.filter((id) => id !== eventId)
        : [...withoutAll, eventId];
    });
  };

  const toggleAllShownSports = () => {
    const ids = visibleSportsEvents.map((event) => event.id);
    const allSelected = ids.length > 0 && ids.every((id) => tempSportsSelection.includes(id));
    setTempSportsSelection(
      allSelected
        ? tempSportsSelection.filter((id) => !ids.includes(id))
        : Array.from(new Set([
          ...tempSportsSelection.filter((id) => id !== 'all-sports'),
          ...ids
        ]))
    );
  };

  const handleUserSelect = (user) => {
    console.log('User selected:', user);
    setSelectedUser(user);
    setSearchTerm('');
    setShowUserSearch(false);
    setEditingLimit(null);
    setGameSearchTerm('');
    setBetLimits([]); // Clear previous limits while loading new ones
    
    // Set initial currency based on user's balances
    const userCurrencies = Object.keys(user.balances || {});
    setLimitForm({
      type: '',
      sportsEventIds: [],
      gameIds: [],
      providerIds: [],
      currency: userCurrencies[0] || '',
      maxBet: '',
      minBet: ''
    });
  };

  /** Jump to an ancestor to see everything that is set on it. */
  const handleOpenUser = (username) => {
    const user = flatUsers.find((candidate) => candidate.username === username);
    if (!user) {
      showNotification('warning', t('common.warning'), t('limits.accountNotFound', 'Could not open that account'));
      return;
    }
    handleUserSelect(user);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // -------------------------------------------------------------------------
  // Acting on a refusal
  // -------------------------------------------------------------------------

  /**
   * Open the limit editor on the rule behind a refused stake.
   *
   * The refusal is handed over described as the bet it was — which scope, which
   * player, which currency — so the editor works out the same options it would
   * offer from a live bet. `targetUsername` is only the starting point: the
   * editor still lets the limit be moved up or down the chain from there.
   */
  const handleSetLimitFromEvent = (event, targetUsername) => {
    const player = event?.player || {};
    if (!player.username) {
      showNotification('warning', t('common.warning'),
        t('limits.eventHasNoPlayer', 'This refusal is not attached to an account'));
      return;
    }

    const node = flatUsers.find((candidate) => candidate.username === player.username);
    const breach = getBreachMeta(event);
    const currency = event?.transaction?.currency;

    setLimitFromEvent({
      event,
      bet: buildBetFromEvent(event),
      player: {
        username: player.username,
        name: node?.name || player.username,
        type: node?.type || 'player'
      },
      currency,
      // The editor shows what is configured today; without this it would not
      // say what went wrong, which is the whole reason this was opened.
      notice: `@${player.username} — ${breach.label.toLowerCase()} on ${formatDateTime(event.occurredAtMs)}. ${event.headline}`,
      suggestion: buildLimitSuggestion(event),
      defaultTargetUsername: targetUsername,
      // Start on the scope that did the refusing, not the narrowest one: a cap
      // on all roulette should open as all roulette, not as the one variant.
      preferredScopeKey: getPreferredScopeKey(event)
    });
  };

  /** Leave the history and open an account's own limits. */
  const handleOpenUserFromHistory = (username) => {
    const user = flatUsers.find((candidate) => candidate.username === username);
    if (!user) {
      showNotification('warning', t('common.warning'),
        t('limits.accountNotFound', 'Could not open that account'));
      return;
    }
    setActiveTab('limits');
    handleUserSelect(user);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAddLimit = () => {
    setEditingLimit(null);
    setGameSearchTerm('');
    setLimitForm({
      type: '', // Start with no type selected
      sportsEventIds: [],
      gameIds: [],
      providerIds: [],
      currency: availableCurrencies[0] || 'USD',
      maxBet: '',
      minBet: ''
    });
    setShowAdvancedOptions(false);
    setShowLimitModal(true);
  };

  const handleEditLimit = (limit) => {
    setEditingLimit(limit);
    setGameSearchTerm('');
    
    // Determine the type based on limitType or fallback to gameId
    let type = 'casino';
    let gameIds = [];
    let providerIds = [];
    let sportsEventIds = [];
    
    if (limit.limitType === 'sports' || limit.limitType === 'sports-event' || limit.gameId === 'sports' || limit.identifier === 'sports' || limit.identifier?.startsWith('sports:')) {
      type = 'sports';
      // Extract sports event ID if present. An edit is always one record.
      let sportsEventId = '';
      if (limit.identifier && limit.identifier.includes(':')) {
        sportsEventId = limit.identifier.split(':')[1];
      } else if (limit.sportsEventId) {
        sportsEventId = limit.sportsEventId;
      } else if (limit.identifier === 'sports' || limit.gameId === 'sports') {
        sportsEventId = 'all-sports';
      }
      sportsEventIds = sportsEventId ? [sportsEventId] : [];
    } else if (limit.limitType === 'provider') {
      // Provider limit
      type = 'casino';
      providerIds = [limit.identifier || limit.providerId];
    } else if (limit.limitType === 'universal' || limit.gameId === 'all' || limit.identifier === 'all') {
      // Universal limit - treat as all providers
      type = 'casino';
      providerIds = ['all-providers'];
    } else if (limit.limitType === 'game-general' || limit.limitType === 'game' || limit.gameId || limit.identifier) {
      // Game limit (including general games like blackjack/roulette all variants)
      type = 'casino';
      gameIds = [limit.identifier || limit.gameId];
    } else {
      // Fallback - treat as game
      type = 'casino';
      gameIds = [limit.identifier || limit.gameId || ''];
    }
    
    setLimitForm({
      type,
      sportsEventIds,
      gameIds,
      providerIds,
      currency: limit.currency || availableCurrencies[0] || 'USD',
      // 0 is a real value (blocked), so it must survive the round trip
      maxBet: limit.maxBet != null ? limit.maxBet.toString() : '',
      minBet: limit.minBet ? limit.minBet.toString() : ''
    });
    // Only reveal the optional minimum when this limit actually carries one.
    setShowAdvancedOptions(!!limit.minBet);
    setShowLimitModal(true);
  };

  /**
   * An inherited limit belongs to the ancestor it was set on, so editing it
   * moves the page onto that account first — the save goes wherever the
   * selected user points.
   */
  const handleEditInheritedLimit = (parentUsername, limit) => {
    const parentUser = flatUsers.find((candidate) => candidate.username === parentUsername);
    if (!parentUser) {
      showNotification('warning', t('common.warning'), t('limits.accountNotFound', 'Could not open that account'));
      return;
    }

    setSelectedUser(parentUser);
    setSearchTerm('');
    setShowUserSearch(false);
    setBetLimits([]);
    handleEditLimit(limit);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseModal = () => {
    setShowLimitModal(false);
    setEditingLimit(null);
    setGameSearchTerm('');
    setIsSavingLimit(false); // Reset saving state
    setShowAdvancedOptions(false);
    setLimitForm({
      type: '',
      sportsEventIds: [],
      gameIds: [],
      providerIds: [],
      currency: availableCurrencies[0] || 'USD',
      maxBet: '',
      minBet: ''
    });
  };

  const handleSaveLimit = async () => {
    console.log('handleSaveLimit called with form:', limitForm);
    
    // Validation
    if (!limitForm.type) {
      showNotification('warning', t('common.warning'), t('limits.selectLimitType', 'Please select a limit type'));
      return;
    }
    
    if (limitForm.type === 'sports' && (limitForm.sportsEventIds || []).length === 0) {
      showNotification('warning', t('common.warning'), t('limits.selectSportsEvent', 'Please select a sports event or category'));
      return;
    }
    
    if (limitForm.type === 'casino' && (limitForm.gameIds.length === 0 && limitForm.providerIds.length === 0)) {
      showNotification('warning', t('common.warning'), t('limits.selectGamesOrProviders', 'Please select games or providers'));
      return;
    }
    
    if (!limitForm.currency || limitForm.maxBet === '') {
      showNotification('warning', t('common.warning'), t('limits.fillAllFields'));
      return;
    }
    
    const maxBet = parseFloat(limitForm.maxBet);
    
    // 0 is allowed and means "blocked": the scope is closed and no bet is taken.
    if (isNaN(maxBet) || maxBet < 0) {
      showNotification('warning', t('common.warning'), t('limits.invalidAmount'));
      return;
    }
    
    const minBet = limitForm.minBet ? parseFloat(limitForm.minBet) : 0;
    
    if (isNaN(minBet) || minBet < 0) {
      showNotification('warning', t('common.warning'), t('limits.invalidAmount'));
      return;
    }
    
    if (maxBet === 0 && minBet > 0) {
      showNotification('warning', t('common.warning'),
        t('limits.blockedNeedsNoMin', 'A maximum of 0 already blocks this scope, so the minimum must be 0 too'));
      return;
    }
    
    // A min above the max stores a limit that rejects every bet in the scope
    if (minBet > maxBet) {
      showNotification('warning', t('common.warning'),
        t('limits.minAboveMax', 'The minimum bet cannot be higher than the maximum bet'));
      return;
    }
    
    if (maxBet === 0) {
      const sportsNames = (limitForm.sportsEventIds || []).map((id) =>
        getAllSportsEvents(customSportsEvents).find((event) => event.id === id)?.name || id
      );
      const scopeLabel = limitForm.type === 'sports'
        ? (sportsNames.length > 1
          ? `${sportsNames.length} competitions (${sportsNames.slice(0, 3).join(', ')}${sportsNames.length > 3 ? '…' : ''})`
          : (sportsNames[0] || ''))
        : t('limits.selectedGames', 'the selected games');
      const confirmed = await confirm({
        title: t('limits.blocked', 'Blocked'),
        message: t('limits.confirmBlock',
          'This blocks all betting on {{scope}} in {{currency}}. Nobody under this account will be able to place a bet on it. Continue?')
          .replace('{{scope}}', scopeLabel)
          .replace('{{currency}}', limitForm.currency),
        confirmLabel: t('limits.blockIt', 'Block it'),
        cancelLabel: t('common.cancel', 'Cancel'),
        tone: 'danger'
      });
      if (!confirmed) return;
    }
    
    console.log('Validation passed, saving limit...');
    setIsSavingLimit(true);
    
    try {
      // Determine user type from selectedUser
      const userType = selectedUser.type || 'player';
      
      // If editing, update single limit
      if (editingLimit) {
        let limitData = {
          id: editingLimit.id,
          currency: limitForm.currency,
          maxBet,
          minBet
        };
        
        if (limitForm.type === 'sports') {
          const eventId = limitForm.sportsEventIds[0];
          const sportsEvent = getAllSportsEvents(customSportsEvents).find(e => e.id === eventId);
          const isAllSports = eventId === 'all-sports';
          
          limitData.limitType = isAllSports ? 'sports' : 'sports-event';
          limitData.identifier = isAllSports ? 'sports' : `sports:${eventId}`;
          limitData.sportsEventId = eventId;
          limitData.sportsEventName = sportsEvent?.name || eventId;
          limitData.sportsEventCategory = sportsEvent?.category || 'other';
          limitData.gameId = 'sports'; // Keep for backward compatibility
        } else if (limitForm.providerIds.length > 0) {
          if (limitForm.providerIds[0] === 'all-providers') {
            limitData.limitType = 'universal';
            limitData.identifier = 'all';
            limitData.gameId = 'all'; // Keep for backward compatibility
          } else {
            limitData.limitType = 'provider';
            limitData.identifier = limitForm.providerIds[0];
            limitData.providerId = limitForm.providerIds[0]; // Add for backend compatibility
          }
        } else if (limitForm.gameIds.length > 0) {
          if (limitForm.gameIds[0] === 'all-games') {
            limitData.limitType = 'universal';
            limitData.identifier = 'all';
            limitData.gameId = 'all'; // Keep for backward compatibility
          } else {
            // Check if it's a general game type
            const isGeneralGame = limitForm.gameIds[0] === 'blackjack' || limitForm.gameIds[0] === 'roulette';
            limitData.limitType = isGeneralGame ? 'game-general' : 'game';
            limitData.identifier = limitForm.gameIds[0];
            limitData.gameId = limitForm.gameIds[0]; // Keep for backward compatibility
          }
        }
        
        const result = await saveBetLimit(selectedUser.username, limitData, userType);
        console.log('Save limit result:', result);
        
        if (result.success) {
          showNotification('success', t('common.success'), t('limits.limitUpdated'));
        } else {
          showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
          return;
        }
      } else if (limitForm.type === 'sports') {
        // One stored limit per competition, same amount on each. Custom events
        // have to be in scope here too, or an admin-added league is stored
        // with its id as its name.
        const catalog = getAllSportsEvents(customSportsEvents);
        const limits = limitForm.sportsEventIds.map((eventId) => {
          const sportsEvent = catalog.find((event) => event.id === eventId);
          const isAllSports = eventId === 'all-sports';
          return {
            limitType: isAllSports ? 'sports' : 'sports-event',
            identifier: isAllSports ? 'sports' : `sports:${eventId}`,
            sportsEventId: eventId,
            sportsEventName: sportsEvent?.name || eventId,
            sportsEventCategory: sportsEvent?.category || 'other',
            gameId: 'sports',
            currency: limitForm.currency,
            maxBet,
            minBet
          };
        });

        const result = limits.length > 1
          ? await saveBulkBetLimits(selectedUser.username, limits, userType)
          : await saveBetLimit(selectedUser.username, limits[0], userType);
        console.log('Save sports limit result:', result);

        if (result.success) {
          showNotification('success', t('common.success'),
            limits.length > 1
              ? t('limits.limitsAdded', { count: limits.length })
              : t('limits.sportsLimitAdded', 'Sports bet limit added successfully'));
        } else {
          showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
          return;
        }
      } else {
        // Check if all games is selected
        if (limitForm.gameIds.includes('all-games')) {
          // Create a universal limit for ALL games
          const limitData = {
            limitType: 'universal',
            identifier: 'all',
            gameId: 'all', // Keep for backward compatibility
            currency: limitForm.currency,
            maxBet,
            minBet
          };
          const result = await saveBetLimit(selectedUser.username, limitData, userType);
          console.log('Save universal limit result:', result);
          
          if (result.success) {
            showNotification('success', t('common.success'), 
              t('limits.universalLimitAdded', 'Universal bet limit applied to ALL games'));
          } else {
            showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
            return;
          }
        } else {
          // Check if all-providers is selected
          if (limitForm.providerIds.includes('all-providers')) {
            // Create a universal limit for ALL games/providers
            const limitData = {
              limitType: 'universal',
              identifier: 'all',
              gameId: 'all', // Keep for backward compatibility
              currency: limitForm.currency,
              maxBet,
              minBet
            };
            const result = await saveBetLimit(selectedUser.username, limitData, userType);
            console.log('Save all-providers limit result:', result);
            
            if (result.success) {
              showNotification('success', t('common.success'), 
                t('limits.universalLimitAdded', 'Universal bet limit applied to ALL games'));
            } else {
              showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
              return;
            }
          } else {
            // Handle provider-based limits
            if (limitForm.providerIds.length > 0) {
              // Create provider-based limits (one for each selected provider)
              const limits = limitForm.providerIds.map(providerId => ({
                limitType: 'provider',
                identifier: providerId,
                providerId: providerId, // Add providerId for backend compatibility
                currency: limitForm.currency,
                maxBet,
                minBet
              }));
              
              if (limits.length > 1) {
                const result = await saveBulkBetLimits(selectedUser.username, limits, userType);
                console.log('Save bulk provider limits result:', result);
                
                if (result.success) {
                  showNotification('success', t('common.success'), 
                    t('limits.providerLimitsAdded', `Provider limits added successfully (${limits.length} providers)`));
                } else {
                  showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
                  return;
                }
              } else {
                const result = await saveBetLimit(selectedUser.username, limits[0], userType);
                console.log('Save single provider limit result:', result);
                
                if (result.success) {
                  showNotification('success', t('common.success'), 
                    t('limits.providerLimitAdded', 'Provider limit added successfully'));
                } else {
                  showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
                  return;
                }
              }
            } else if (limitForm.gameIds.length > 0) {
              // Handle game-based limits
              const limits = limitForm.gameIds.map(gameId => {
                // Check if it's a general game type (blackjack/roulette all variants)
                const isGeneralGame = gameId === 'blackjack' || gameId === 'roulette';
                
                return {
                  limitType: isGeneralGame ? 'game-general' : 'game',
                  identifier: gameId,
                  gameId, // Keep for backward compatibility
                  currency: limitForm.currency,
                  maxBet,
                  minBet
                };
              });
              
              if (limits.length > 1) {
                const result = await saveBulkBetLimits(selectedUser.username, limits, userType);
                console.log('Save bulk game limits result:', result);
                
                if (result.success) {
                  showNotification('success', t('common.success'), 
                    t('limits.limitsAdded', { count: limits.length }));
                } else {
                  showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
                  return;
                }
              } else {
                const result = await saveBetLimit(selectedUser.username, limits[0], userType);
                console.log('Save single game limit result:', result);
                
                if (result.success) {
                  showNotification('success', t('common.success'), t('limits.limitAdded'));
                } else {
                  showNotification('error', t('common.error'), result.error || t('limits.errorSavingLimit'));
                  return;
                }
              }
            }
          }
        }
      }
      
      // Long lists start collapsed, so open the bucket that was just written to
      // instead of sending the admin looking for what they saved.
      const savedGroup =
        limitForm.type === 'sports'
          ? 'sports'
          : limitForm.providerIds.includes('all-providers') || limitForm.gameIds.includes('all-games')
          ? 'universal'
          : limitForm.providerIds.length > 0
          ? 'provider'
          : 'game';
      setExpandedGroups((prev) => ({ ...prev, [savedGroup]: true }));

      // Refresh limits
      await loadBetLimits();
      
      // Close modal and reset form
      handleCloseModal();
    } catch (error) {
      console.error('Error saving limit:', error);
      showNotification('error', t('common.error'), error.message || t('limits.errorSavingLimit'));
    } finally {
      setIsSavingLimit(false);
    }
  };

  const handleDeleteLimit = async (limit) => {
    const confirmed = await confirm({
      title: t('limits.deleteLimit', 'Delete limit'),
      message: t('limits.confirmDelete'),
      confirmLabel: t('common.delete', 'Delete'),
      cancelLabel: t('common.cancel', 'Cancel'),
      tone: 'danger'
    });
    if (!confirmed) return;

    // Without an id the API has nothing to match on and answers 404, so say why
    // rather than firing a request that cannot succeed.
    if (!limit.id) {
      showNotification('error', t('common.error'),
        t('limits.limitMissingId', 'This limit has no id and cannot be deleted. Refresh the page and try again.'));
      return;
    }

    try {
      const result = await deleteBetLimit(selectedUser.username, limit.id);

      if (result.success) {
        showNotification('success', t('common.success'), t('limits.limitDeleted'));
        await loadBetLimits();
      } else {
        // deleteBetLimit swallows API errors into { success: false }, so without
        // this branch a 404 or an expired session deleted nothing and said nothing.
        console.error('Delete rejected by the API:', result);
        showNotification('error', t('common.error'), result.error || t('limits.errorDeletingLimit'));
      }
    } catch (error) {
      console.error('Error deleting limit:', error);
      showNotification('error', t('common.error'), error.message || t('limits.errorDeletingLimit'));
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (isLoading) {
    return <Loading />;
  }

  // Show breakglass warning if active
  if (isBreakglassActive) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <ShieldCheckIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
                {t('limits.title', 'Bet Limits')}
              </h1>
            </div>
          </div>
          
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-300 dark:border-yellow-700 rounded-xl p-8 text-center">
            <div className="flex flex-col items-center gap-4">
              <svg className="w-16 h-16 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {t('limits.breakglassMode', 'Breakglass Mode Active')}
                </h2>
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  {t('limits.breakglassModeDescription', 'Bet limits management is temporarily disabled for security reasons.')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20 sm:pb-6">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-6">
        {/* Header - Compact on Mobile */}
        <div className="mb-3 sm:mb-4 md:mb-6">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <ShieldCheckIcon className="w-6 h-6 sm:w-7 sm:h-7 text-blue-600 dark:text-blue-400" />
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
                {t('limits.title', 'Bet Limits')}
              </h1>
            </div>
            <button
              onClick={() => toggleSection('info')}
              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors touch-manipulation"
              title={t('limits.howItWorks', 'How it works')}
            >
              <InformationCircleIcon className="w-5 h-5" />
            </button>
          </div>
          
          {/* Info Section */}
          {expandedSections.info && (
            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2 flex items-center gap-2">
                <ChartBarIcon className="w-4 h-4" />
                {t('limits.limitHierarchy', 'Limit Hierarchy')}
              </h3>
              <div className="space-y-2 text-xs sm:text-sm text-blue-800 dark:text-blue-200">
                <div className="flex items-start gap-2">
                  <span className="inline-block px-2 py-0.5 bg-blue-200 dark:bg-blue-800 rounded text-xs font-medium">1</span>
                  <p><strong>{t('limits.platformLimits', 'Platform Limits')}:</strong> {t('limits.platformLimitsDesc', 'Default limits that apply to all users across the platform')}</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="inline-block px-2 py-0.5 bg-blue-200 dark:bg-blue-800 rounded text-xs font-medium">2</span>
                  <p><strong>{t('limits.countryLimits', 'Country Limits')}:</strong> {t('limits.countryLimitsDesc', 'Override platform limits for specific countries (e.g., Kenya, Uganda)')}</p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="inline-block px-2 py-0.5 bg-blue-200 dark:bg-blue-800 rounded text-xs font-medium">3</span>
                  <p><strong>{t('limits.userLimits', 'User-Specific Limits')}:</strong> {t('limits.userLimitsDesc', 'Override country limits for individual players or agents')}</p>
                </div>
                <div className="mt-3 pt-3 border-t border-blue-300 dark:border-blue-700">
                  <p className="text-xs italic">
                    💡 {t('limits.limitNote', 'Higher priority limits override lower ones. User-specific limits have the highest priority.')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Two views of the same rules: what is configured, and what those
            rules have actually refused. */}
        <div className="mb-3 sm:mb-4 grid grid-cols-2 gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
          {[
            { id: 'limits', Icon: ShieldCheckIcon, label: t('limits.tabLimits', 'Limits') },
            { id: 'history', Icon: ClockIcon, label: t('limits.tabHistory', 'History') }
          ].map(({ id, Icon, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                activeTab === id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* User picker. It folds away once someone is picked, so on a phone the
            limits themselves start near the top instead of under a search box
            that has already done its job. */}
        {(!selectedUser || showUserSearch) && (
          <div className="mb-3 sm:mb-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('limits.searchUsersPlaceholder', 'Search users (admin, country, agent, player)...')}
                className="w-full pl-10 pr-11 py-3 sm:py-2.5 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
              />
              {(searchTerm || selectedUser) && (
                <button
                  onClick={() => (searchTerm ? setSearchTerm('') : setShowUserSearch(false))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation"
                  title={t('common.clear', 'Clear')}
                >
                  <XMarkIcon className="w-5 h-5 text-gray-400" />
                </button>
              )}
            </div>

            {searchTerm && filteredUsers.length > 0 && (
              <div className="mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 max-h-[60vh] overflow-y-auto">
                <div className="p-2 space-y-1">
                  {filteredUsers.map((user) => (
                    <button
                      key={user.username}
                      onClick={() => handleUserSelect(user)}
                      className="w-full text-left p-3 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors active:bg-blue-100 dark:active:bg-blue-900/30 touch-manipulation"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg shrink-0 ${getTypeColor(user.type)}`}>
                          {React.createElement(getTypeIcon(user.type), { className: 'w-5 h-5' })}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {user.name || user.username}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            @{user.username} · {t(`userTypes.${user.type}`, user.type)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {searchTerm && filteredUsers.length === 0 && (
              <div className="mt-2 p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('limits.noUsersFound', 'No users found')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Who is being looked at, and how many rules are in play for them */}
        {selectedUser && !showUserSearch && (
          <div className="mb-3 sm:mb-4 rounded-xl border-2 border-blue-200 dark:border-blue-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-3 shadow-sm">
            <div className="flex items-center gap-2.5">
              <div className={`p-2.5 rounded-lg shrink-0 shadow-sm ${getTypeColor(selectedUser.type)}`}>
                {React.createElement(getTypeIcon(selectedUser.type), { className: 'w-6 h-6' })}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {selectedUser.name || selectedUser.username}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                  @{selectedUser.username} · {t(`userTypes.${selectedUser.type}`, selectedUser.type)}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUserSearch(true);
                  setSearchTerm('');
                }}
                className="shrink-0 px-3 py-2 text-xs font-medium text-blue-700 dark:text-blue-300 bg-white/70 dark:bg-gray-800/70 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-colors touch-manipulation"
              >
                {t('limits.changeUser', 'Change')}
              </button>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setBetLimits([]);
                  setParentLimits([]);
                }}
                className="shrink-0 p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors touch-manipulation"
                title={t('common.clear', 'Clear')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className={`mt-2.5 flex-wrap items-center gap-1.5 ${activeTab === 'limits' ? 'flex' : 'hidden'}`}>
              <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-200 border border-blue-200/70 dark:border-blue-800">
                {t('limits.ownLimitsCount', 'Own')}: {betLimits.length}
              </span>
              <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-white/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-200 border border-blue-200/70 dark:border-blue-800">
                {t('limits.inheritedCount', 'Inherited')}: {inheritedCount}
              </span>
              {limitCounts.blocked > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  <NoSymbolIcon className="w-3 h-3" />
                  {limitCounts.blocked} {t('limits.blocked', 'Blocked')}
                </span>
              )}
            </div>
          </div>
        )}
        
        <div className="space-y-3 sm:space-y-4">
          {/* Main Content Area */}
          {activeTab === 'history' ? (
            <LimitHistory
              flatUsers={flatUsers}
              customSportsEvents={customSportsEvents}
              selectedUser={selectedUser}
              onSetLimit={handleSetLimitFromEvent}
              onOpenUser={handleOpenUserFromHistory}
            />
          ) : selectedUser ? (
            <>
              {/* Everything inherited from further up the chain. One collapsible
                  block per ancestor, and every amount is a button that opens
                  that limit on the account it belongs to. */}
              {inheritedGroups.length > 0 && (
                <section className="bg-white dark:bg-gray-800 rounded-xl border border-blue-200 dark:border-blue-800 shadow-sm overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleSection('inherited')}
                    className="w-full flex items-center gap-2.5 px-3 sm:px-4 py-3 bg-blue-50/70 dark:bg-blue-900/20 text-left touch-manipulation"
                  >
                    <ArrowUpCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-blue-900 dark:text-blue-100">
                        {t('limits.limitsFromAbove', 'Limits from above')}
                      </span>
                      <span className="block text-[11px] text-blue-700/80 dark:text-blue-300/80">
                        {t('limits.tapToChangeAbove', 'Tap an amount to change it where it is set')}
                      </span>
                    </span>
                    <span className="shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-200/70 text-blue-900 dark:bg-blue-800 dark:text-blue-100">
                      {inheritedCount}
                    </span>
                    <ChevronDownIcon
                      className={`w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 transition-transform ${
                        expandedSections.inherited ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {expandedSections.inherited && (
                    <div className="border-t border-blue-100 dark:border-blue-900 divide-y divide-gray-100 dark:divide-gray-700">
                      {inheritedGroups.map((parent, parentIndex) => {
                        const ParentIcon = getTypeIcon(parent.type);
                        const isOpen = isParentExpanded(parent.username, parentIndex);

                        return (
                          <div key={parent.username}>
                            <div className="flex items-center gap-1 px-2 sm:px-3">
                              <button
                                type="button"
                                onClick={() => toggleParent(parent.username, parentIndex)}
                                className="flex-1 min-w-0 flex items-center gap-2.5 py-3 text-left touch-manipulation"
                              >
                                <span className={`p-1.5 rounded-lg shrink-0 ${getTypeColor(parent.type)}`}>
                                  <ParentIcon className="w-4 h-4" />
                                </span>
                                <span className="flex-1 min-w-0">
                                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {parent.name}
                                  </span>
                                  <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                    {getTypeLabel(parent.type)} · @{parent.username}
                                  </span>
                                </span>
                                {parent.blockedCount > 0 && (
                                  <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                    {parent.blockedCount}
                                  </span>
                                )}
                                <span className="shrink-0 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                                  {parent.limitCount}
                                </span>
                                <ChevronDownIcon
                                  className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleOpenUser(parent.username)}
                                title={t('limits.manageThisAccount', 'Manage this account')}
                                className="shrink-0 p-2 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors touch-manipulation"
                              >
                                <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                              </button>
                            </div>

                            {isOpen && (
                              <div className="px-3 pb-3 space-y-2">
                                {parent.groups.map((group) => (
                                  <div key={group.id} className="space-y-1.5">
                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                                      {t(`limits.group_${group.id}`, group.id)}
                                    </p>
                                    {group.scopes.map((scope) => (
                                      <div
                                        key={scope.key}
                                        className="rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700 p-2.5"
                                      >
                                        <div className="flex items-center gap-2 mb-1.5">
                                          <ScopeGlyph scope={scope} />
                                          <p className="flex-1 min-w-0 text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                            {scope.name}
                                          </p>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                          {scope.limits.map((limit, limitIndex) => (
                                            <button
                                              key={limit.id || `${scope.key}-${limitIndex}`}
                                              type="button"
                                              onClick={() => handleEditInheritedLimit(parent.username, limit)}
                                              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors touch-manipulation ${
                                                isBlockingLimit(limit)
                                                  ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                  : 'border-gray-200 bg-white text-gray-700 hover:border-blue-400 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-blue-600'
                                              }`}
                                            >
                                              <span className="opacity-60">{limit.currency}</span>
                                              <span className="font-semibold">
                                                {isBlockingLimit(limit)
                                                  ? t('limits.blocked', 'Blocked')
                                                  : formatCurrency(limit.maxBet, limit.currency)}
                                              </span>
                                              <PencilIcon className="w-3 h-3 opacity-50" />
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}

              {inheritedGroups.length === 0 && selectedUser.parentPath?.length > 0 && (
                <p className="flex items-center gap-1.5 px-1 text-xs text-gray-500 dark:text-gray-400">
                  <ArrowUpCircleIcon className="w-4 h-4 shrink-0" />
                  {t('limits.nothingAbove', 'Nothing is limited further up the chain')}
                </p>
              )}

              {/* The limits set on this account. Grouped into buckets that
                  collapse, because a phone cannot show forty rules at once and
                  an admin is usually after one of them. */}
              <section className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
                {/* Sticks under the app bar so the filters and the add button
                    stay reachable while scrolling a long list. */}
                <div className="sticky top-header z-20 bg-white dark:bg-gray-800 rounded-t-xl border-b border-gray-200 dark:border-gray-700 px-3 sm:px-4 pt-3 pb-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <ShieldCheckIcon className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {t('limits.userSpecificLimits', 'User-Specific Limits')}
                      </h2>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                        {t('limits.ownLimitsHint', 'Set here — overrides everything above')}
                      </p>
                    </div>
                    <button
                      onClick={handleAddLimit}
                      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors shadow-sm touch-manipulation"
                    >
                      <PlusIcon className="w-4 h-4" />
                      {t('common.add', 'Add')}
                    </button>
                  </div>

                  {betLimits.length > 0 && (
                    <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
                      {LIMIT_FILTERS.filter(
                        (filter) => filter.id === 'all' || limitCounts[filter.id] > 0
                      ).map((filter) => {
                        const FilterIcon = filter.Icon;
                        const isActive = filterType === filter.id;
                        return (
                          <button
                            key={filter.id}
                            onClick={() => setFilterType(filter.id)}
                            className={`shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full transition-colors touch-manipulation ${
                              isActive
                                ? filter.active
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                          >
                            <FilterIcon className="w-3.5 h-3.5" />
                            {t(filter.labelKey, filter.labelDefault)} ({limitCounts[filter.id]})
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {betLimits.length > 6 && (
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1 min-w-0">
                        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={limitSearch}
                          onChange={(e) => setLimitSearch(e.target.value)}
                          placeholder={t('limits.filterLimits', 'Filter by game, league or currency...')}
                          className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        {limitSearch && (
                          <button
                            onClick={() => setLimitSearch('')}
                            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 touch-manipulation"
                          >
                            <XMarkIcon className="w-4 h-4 text-gray-400" />
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => setAllGroupsExpanded(!areAllGroupsExpanded)}
                        className="shrink-0 inline-flex items-center gap-1 px-2.5 py-2 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors touch-manipulation"
                      >
                        {areAllGroupsExpanded ? (
                          <ChevronUpIcon className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronDownIcon className="w-3.5 h-3.5" />
                        )}
                        {areAllGroupsExpanded
                          ? t('limits.collapseAll', 'Collapse')
                          : t('limits.expandAll', 'Expand')}
                      </button>
                    </div>
                  )}
                </div>

                <div className="p-3 sm:p-4 space-y-2.5">
                  {isLoadingLimits ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : limitGroups.length > 0 ? (
                    limitGroups.map((group) => {
                      const meta = GROUP_META[group.id];
                      const GroupIcon = meta.Icon;
                      const isOpen = isGroupExpanded(group.id);

                      return (
                        <div key={group.id} className={`rounded-xl border ${meta.border} overflow-hidden`}>
                          <button
                            type="button"
                            onClick={() => toggleGroup(group.id)}
                            className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left touch-manipulation ${meta.header}`}
                          >
                            <GroupIcon className={`w-5 h-5 shrink-0 ${meta.iconColor}`} />
                            <span className="flex-1 min-w-0">
                              <span className="block text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                {t(`limits.group_${group.id}`, group.id)}
                              </span>
                              <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                {group.scopes.length} {t('limits.rules', 'rules')}
                              </span>
                            </span>
                            {group.blockedCount > 0 && (
                              <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                                <NoSymbolIcon className="w-3 h-3" />
                                {group.blockedCount}
                              </span>
                            )}
                            <span className={`shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full ${meta.chip}`}>
                              {group.limitCount}
                            </span>
                            <ChevronDownIcon
                              className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                            />
                          </button>

                          {isOpen && (
                            <div className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                              {group.scopes.map((scope) => (
                                <div key={scope.key} className="px-3 py-2.5">
                                  <div className="flex items-center gap-2 mb-2">
                                    <ScopeGlyph scope={scope} />
                                    <p className="flex-1 min-w-0 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                      {scope.name}
                                    </p>
                                    {scope.limits.length > 1 && (
                                      <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
                                        {scope.limits.length} {t('limits.currencies', 'currencies')}
                                      </span>
                                    )}
                                  </div>

                                  <div className="space-y-1.5">
                                    {scope.limits.map((limit, limitIndex) => (
                                      <div key={limit.id || `${scope.key}-${limitIndex}`} className="flex items-center gap-1.5">
                                        <button
                                          type="button"
                                          onClick={() => handleEditLimit(limit)}
                                          className="flex-1 min-w-0 flex items-center gap-2 px-2.5 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 hover:border-blue-400 hover:bg-blue-50/60 dark:hover:border-blue-600 dark:hover:bg-blue-900/20 transition-colors touch-manipulation"
                                        >
                                          <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
                                            {limit.currency}
                                          </span>
                                          {isBlockingLimit(limit) ? (
                                            <span className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 dark:text-red-400">
                                              <NoSymbolIcon className="w-4 h-4" />
                                              {t('limits.blocked', 'Blocked')}
                                            </span>
                                          ) : (
                                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                              {formatCurrency(limit.maxBet, limit.currency)}
                                            </span>
                                          )}
                                          {limit.minBet > 0 && (
                                            <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                              {t('limits.minShort', 'min')} {formatCurrency(limit.minBet, limit.currency)}
                                            </span>
                                          )}
                                          <PencilIcon className="w-4 h-4 text-gray-400 ml-auto shrink-0" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleDeleteLimit(limit)}
                                          title={t('common.delete', 'Delete')}
                                          className="shrink-0 p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors touch-manipulation"
                                        >
                                          <TrashIcon className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : betLimits.length > 0 ? (
                    <div className="text-center py-8 px-4">
                      <FunnelIcon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        {t('limits.noMatchingLimits', 'No limits match this filter')}
                      </p>
                      <button
                        onClick={() => {
                          setFilterType('all');
                          setLimitSearch('');
                        }}
                        className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline touch-manipulation"
                      >
                        {t('limits.clearFilters', 'Clear filters')}
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-8 px-4">
                      <ShieldCheckIcon className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-2">
                        {t('limits.noLimitsSet', 'No user-specific limits set')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-sm mx-auto">
                        {inheritedCount > 0
                          ? t('limits.parentLimitsApply', 'Parent hierarchy limits shown above will apply. You can override them by adding user-specific limits here.')
                          : t('limits.noLimitsDescription', 'No limits are currently restricting this user. You can set custom limits to control betting behavior.')}
                      </p>
                      <button
                        onClick={handleAddLimit}
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors inline-flex items-center gap-2 font-medium shadow-sm touch-manipulation"
                      >
                        <PlusIcon className="w-5 h-5" />
                        {t('limits.addFirstLimit', 'Add First Limit')}
                      </button>
                    </div>
                  )}
                </div>
              </section>
              </>
            ) : (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8">
              <div className="text-center">
                <MagnifyingGlassIcon className="w-16 h-16 sm:w-20 sm:h-20 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {t('limits.startSearching', 'Start by searching for a user')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                  {t('limits.searchHint', 'Use the search bar above to find any user in the system')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Setting a limit straight off a refusal in the history. Same editor a
          bet opens, so the limit lands in exactly the same place. */}
      {limitFromEvent && (
        <LimitScopeModal
          isOpen
          bet={limitFromEvent.bet}
          player={limitFromEvent.player}
          userTree={userTree}
          currency={limitFromEvent.currency}
          notice={limitFromEvent.notice}
          suggestion={limitFromEvent.suggestion}
          defaultTargetUsername={limitFromEvent.defaultTargetUsername}
          preferredScopeKey={limitFromEvent.preferredScopeKey}
          onClose={() => setLimitFromEvent(null)}
          onSaved={() => {
            // The account on screen may be the one that just changed.
            if (selectedUser) {
              loadBetLimits();
              loadParentLimits();
            }
          }}
        />
      )}

      {/* Limit Form Modal */}
      {showLimitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-modal flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div 
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col animate-slide-up sm:animate-none"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between shrink-0">
              {/* Mobile drag indicator */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full sm:hidden"></div>
              
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mt-2 sm:mt-0">
                {editingLimit ? t('limits.editLimit', 'Edit Limit') : t('limits.addNewLimit', 'Add New Limit')}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors touch-manipulation"
              >
                <XMarkIcon className="w-6 h-6 sm:w-5 sm:h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4 sm:space-y-6" style={{ position: 'relative', overflowX: 'visible' }}>
              {/* Type Selection - Sports or Casino */}
              {!editingLimit && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('limits.whatTypeOfLimit', 'What type of limit would you like to set?')}
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      type="button"
                      onClick={() => setLimitForm(prev => ({ ...prev, type: 'sports' }))}
                      className={`p-6 rounded-lg border-2 transition-all ${
                        limitForm.type === 'sports'
                          ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <TrophyIcon className="w-8 h-8 mb-2 text-green-600 dark:text-green-400" />
                        <div className={`font-semibold ${
                          limitForm.type === 'sports'
                            ? 'text-green-700 dark:text-green-300'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {t('limits.sports', 'Sports')}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('limits.sportsDescription', 'Set limits for sports betting')}
                        </div>
                      </div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => setLimitForm(prev => ({ ...prev, type: 'casino' }))}
                      className={`p-6 rounded-lg border-2 transition-all ${
                        limitForm.type === 'casino'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <PuzzlePieceIcon className="w-8 h-8 mb-2 text-blue-600 dark:text-blue-400" />
                        <div className={`font-semibold ${
                          limitForm.type === 'casino'
                            ? 'text-blue-700 dark:text-blue-300'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {t('limits.casino', 'Casino')}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {t('limits.casinoDescription', 'Set limits for casino games')}
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Parent Limits Info (Inheritance Helper) */}
              {limitForm.type && parentLimits.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <InformationCircleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                        {t('limits.inheritanceInfo', 'Inheritance Info')}
                      </h4>
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        {t('limits.inheritanceDescription', 'This user inherits {{count}} limit(s) from parent entities. Your new limit will override any parent limits that match.', { count: parentLimits.reduce((sum, p) => sum + p.limits.length, 0) })}
                      </p>
                    </div>
                  </div>
                  <div className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
                    {parentLimits.slice(0, 2).map((parent, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="font-medium">📍 {parent.username}:</span>
                        <span>{parent.limits.length} {parent.limits.length === 1 ? 'limit' : 'limits'}</span>
                      </div>
                    ))}
                    {parentLimits.length > 2 && (
                      <div className="italic">
                        +{parentLimits.length - 2} more parent(s)
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Show fields based on type selection */}
              {limitForm.type && (
                <>
                  {limitForm.type === 'sports' && (
                    <>
                      {/* Sports Event Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('limits.selectSportsEvent', 'Select Sports Event/Category')}
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setTempSportsSelection(limitForm.sportsEventIds || []);
                            setShowSportsEventModal(true);
                          }}
                          className="w-full p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-green-400 dark:hover:border-green-500 transition-all text-left"
                        >
                          {(limitForm.sportsEventIds || []).length > 0 ? (
                            <div className="flex items-start gap-2">
                              {(() => {
                                const allEvents = getAllSportsEvents(customSportsEvents);
                                const selected = (limitForm.sportsEventIds || [])
                                  .map((id) => allEvents.find((event) => event.id === id))
                                  .filter(Boolean);
                                const first = selected[0];
                                return (
                                  <>
                                    {first?.icon ? (
                                      <span className="text-2xl">{first.icon}</span>
                                    ) : (
                                      <TrophyIcon className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
                                    )}
                                    <div className="min-w-0">
                                      <div className="font-medium text-gray-900 dark:text-gray-100">
                                        {selected.length === 1
                                          ? first.name
                                          : fillCount(t('limits.sportsSelectedCount', { count: selected.length }), selected.length)}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {selected.length === 1
                                          ? t(`limits.${first.category}Category`, first.category)
                                          : selected.map((event) => event.name).join(', ')}
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                              <TrophyIcon className="w-5 h-5" />
                              <span>{t('limits.chooseSportsEvent', 'Choose a sports event or category...')}</span>
                            </div>
                          )}
                        </button>
                      </div>
                    </>
                  )}
                  
                  {limitForm.type === 'casino' && (
                    <>
                      {/* Casino Limit Options - Provider OR Games */}
                      <div className="space-y-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            {t('limits.chooseOneOption', 'Choose one option')}
                          </p>
                        </div>
                        
                        {/* Option Cards */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Provider Option Card */}
                          <button
                            type="button"
                            onClick={() => {
                              // Clear games if selecting providers
                              setLimitForm(prev => ({ ...prev, gameIds: [] }));
                              setTempProviderSelection(limitForm.providerIds);
                              setShowProviderModal(true);
                            }}
                            disabled={limitForm.gameIds.length > 0}
                            className={`p-4 rounded-lg border-2 transition-all text-left relative ${
                              limitForm.providerIds.length > 0
                                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                                : limitForm.gameIds.length > 0
                                ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 opacity-50 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-sm'
                            }`}
                          >
                            {limitForm.providerIds.length > 0 && (
                              <div className="absolute -top-2 -right-2">
                                <CheckIcon className="w-6 h-6 text-white bg-purple-500 rounded-full p-1" />
                              </div>
                            )}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Square3Stack3DIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                                <span className="text-lg">Provider Limits</span>
                              </div>
                              {limitForm.providerIds.length > 0 && (
                                <span className="px-2 py-1 text-xs font-medium bg-purple-600 text-white rounded-full">
                                  {limitForm.providerIds.length}
                                </span>
                              )}
                            </div>
                            <h4 className={`font-semibold mb-1 ${
                              limitForm.gameIds.length > 0 
                                ? 'text-gray-500 dark:text-gray-500' 
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              {t('limits.byProvider', 'By Provider')}
                            </h4>
                            <p className={`text-xs ${
                              limitForm.gameIds.length > 0
                                ? 'text-gray-400 dark:text-gray-600'
                                : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {t('limits.providerDesc', 'Apply limit to all games from selected providers')}
                            </p>
                          </button>
                          
                          {/* Games Option Card */}
                          <button
                            type="button"
                            onClick={() => {
                              // Clear providers if selecting games
                              setLimitForm(prev => ({ ...prev, providerIds: [] }));
                              setTempGameSelection(limitForm.gameIds);
                              setShowGameModal(true);
                            }}
                            disabled={limitForm.providerIds.length > 0}
                            className={`p-4 rounded-lg border-2 transition-all text-left relative ${
                              limitForm.gameIds.length > 0
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                                : limitForm.providerIds.length > 0
                                ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 opacity-50 cursor-not-allowed'
                                : 'border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm'
                            }`}
                          >
                            {limitForm.gameIds.length > 0 && (
                              <div className="absolute -top-2 -right-2">
                                <CheckIcon className="w-6 h-6 text-white bg-blue-500 rounded-full p-1" />
                              </div>
                            )}
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <CubeIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                <span className="text-lg">Game Limits</span>
                              </div>
                              {limitForm.gameIds.length > 0 && (
                                <span className="px-2 py-1 text-xs font-medium bg-blue-600 text-white rounded-full">
                                  {limitForm.gameIds.length}
                                </span>
                              )}
                            </div>
                            <h4 className={`font-semibold mb-1 ${
                              limitForm.providerIds.length > 0 
                                ? 'text-gray-500 dark:text-gray-500' 
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              {t('limits.byGames', 'By Individual Games')}
                            </h4>
                            <p className={`text-xs ${
                              limitForm.providerIds.length > 0
                                ? 'text-gray-400 dark:text-gray-600'
                                : 'text-gray-600 dark:text-gray-400'
                            }`}>
                              {t('limits.gamesDesc', 'Select specific games for this limit')}
                            </p>
                          </button>
                        </div>
                      </div>
                      
              {/* Show selected providers summary */}
              {limitForm.providerIds.length > 0 && (
                <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
                      {t('limits.selectedProviders', 'Selected Providers')} ({limitForm.providerIds.length})
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setTempProviderSelection(limitForm.providerIds);
                        setShowProviderModal(true);
                      }}
                      className="text-xs text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300 font-medium"
                    >
                      {t('limits.changeSelection', 'Change')}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {limitForm.providerIds.map(providerId => {
                      const provider = GAME_PROVIDERS.find(p => p.id === providerId);
                      return (
                        <span
                          key={providerId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs"
                        >
                          {provider?.name || providerId}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Show selected games summary with inheritance check */}
              {limitForm.gameIds.length > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                      {limitForm.gameIds.includes('all-games') 
                        ? t('limits.allGamesSelected', 'All Games Selected')
                        : `${t('limits.selectedGames', 'Selected Games')} (${limitForm.gameIds.length})`
                      }
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setTempGameSelection(limitForm.gameIds);
                        setShowGameModal(true);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                    >
                      {t('limits.changeSelection', 'Change')}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {limitForm.gameIds.includes('all-games') ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                        {t('limits.allGames', 'ALL GAMES')}
                      </span>
                    ) : (
                      limitForm.gameIds.slice(0, 5).map(gameId => {
                        const game = gameOptions.find(g => g.id === gameId);
                        // Check if parent has limit for this game
                        const parentHasLimit = parentLimits.some(p => 
                          p.limits.some(l => 
                            (l.limitType === 'game' && l.identifier === gameId) ||
                            (l.limitType === 'game-general' && l.identifier === gameId) ||
                            (l.limitType === 'universal')
                          )
                        );
                        
                        return (
                          <span
                            key={gameId}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                              parentHasLimit 
                                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 ring-1 ring-amber-300 dark:ring-amber-700'
                                : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                            }`}
                            title={parentHasLimit ? t('limits.overridesParent', 'Will override parent limit') : ''}
                          >
                            {game?.icon && <span>{game.icon}</span>}
                            {game?.name || `${t('limits.customGame', 'Custom')} (${gameId})`}
                            {parentHasLimit && <span className="ml-1">⚡</span>}
                          </span>
                        );
                      })
                    )}
                    {limitForm.gameIds.length > 5 && !limitForm.gameIds.includes('all-games') && (
                      <span className="inline-flex items-center px-2 py-1 text-blue-600 dark:text-blue-400 text-xs">
                        +{limitForm.gameIds.length - 5} {t('limits.more', 'more')}
                      </span>
                    )}
                  </div>
                  {limitForm.gameIds.some(gameId => 
                    parentLimits.some(p => 
                      p.limits.some(l => 
                        (l.limitType === 'game' && l.identifier === gameId) ||
                        (l.limitType === 'game-general' && l.identifier === gameId) ||
                        (l.limitType === 'universal')
                      )
                    )
                  ) && (
                    <div className="mt-2 pt-2 border-t border-blue-200 dark:border-blue-700">
                      <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1">
                        <span>⚡</span>
                        {t('limits.willOverrideParent', 'These selections will override parent limits')}
                      </p>
                    </div>
                  )}
                </div>
              )}
              </>
            )}
                  
                  {/* Currency Selection - For both Sports and Casino */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('limits.currency', 'Currency')}
                    </label>
                    <select
                      value={limitForm.currency}
                      onChange={(e) => setLimitForm(prev => ({ ...prev, currency: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">{t('limits.chooseCurrency', 'Choose currency...')}</option>
                      {availableCurrencies.map(currency => (
                        <option key={currency} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Maximum Bet */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('limits.maxBet', 'Maximum Bet')} *
                    </label>
                    <div className="relative">
                      <CurrencyDollarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="number"
                        value={limitForm.maxBet}
                        onChange={(e) => {
                          const maxBet = e.target.value;
                          // A max of 0 blocks the scope outright, which leaves no room for a
                          // floor. Drop any minimum with it so the save cannot fail on a
                          // field the admin can no longer see.
                          const blocksScope = parseFloat(maxBet) === 0;
                          setLimitForm(prev => ({ ...prev, maxBet, minBet: blocksScope ? '' : prev.minBet }));
                          if (blocksScope) setShowAdvancedOptions(false);
                        }}
                        placeholder={t('limits.enterMaxBet', 'Enter maximum bet amount')}
                        min="0"
                        step="0.01"
                        className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    {parseFloat(limitForm.maxBet) === 0 ? (
                      <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
                        <NoSymbolIcon className="w-3.5 h-3.5" />
                        {t('limits.zeroBlocksScope', 'A maximum of 0 blocks this scope entirely — no bet will be accepted')}
                      </p>
                    ) : (
                      <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                        {t('limits.zeroHint', 'Enter 0 to block this scope entirely')}
                      </p>
                    )}
                  </div>

                  {/* Minimum Bet — opt-in, so the common case stays a single amount
                      field and a floor is only ever set deliberately. Hidden while the
                      max blocks the scope, where a minimum has nothing to apply to. */}
                  {parseFloat(limitForm.maxBet) !== 0 && (
                    <div>
                      {showAdvancedOptions ? (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                              {t('limits.minBet', 'Minimum Bet')}
                              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                {t('limits.optional', 'Optional')}
                              </span>
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                setShowAdvancedOptions(false);
                                setLimitForm(prev => ({ ...prev, minBet: '' }));
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 -mr-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-md transition-colors touch-manipulation"
                            >
                              <XMarkIcon className="w-3.5 h-3.5" />
                              {t('limits.removeMinBet', 'Remove')}
                            </button>
                          </div>
                          <div className="relative">
                            <CurrencyDollarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                              type="number"
                              inputMode="decimal"
                              value={limitForm.minBet}
                              onChange={(e) => setLimitForm(prev => ({ ...prev, minBet: e.target.value }))}
                              placeholder={t('limits.enterMinBet', 'Enter minimum bet amount')}
                              min="0"
                              step="0.01"
                              className="w-full pl-10 pr-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                            {t('limits.minBetHelp', 'Set a minimum bet amount to prevent very small bets')}
                          </p>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setShowAdvancedOptions(true)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-600 dark:hover:text-blue-400 hover:bg-blue-50/60 dark:hover:bg-blue-900/20 transition-colors touch-manipulation"
                        >
                          <PlusIcon className="w-4 h-4" />
                          {t('limits.addMinBet', 'Add a minimum bet')}
                          <span className="text-xs font-normal text-gray-400 dark:text-gray-500">
                            {t('limits.optional', 'Optional')}
                          </span>
                        </button>
                      )}
                    </div>
                  )}

                  {/* Summary warning for universal limit only */}
                  {limitForm.type === 'casino' && limitForm.providerIds.includes('all-providers') && (
                    <div className="p-3 rounded-lg border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                      <div className="text-sm text-red-800 dark:text-red-200">
                        <strong>{t('limits.warning', 'Warning')}:</strong> {t('limits.universalLimitWarning', 'This will set a UNIVERSAL limit for ALL games and providers')}
                      </div>
                    </div>
                  )}
                  
                  {/* Sports info message */}
                  {limitForm.type === 'sports' && (
                    <div className="p-3 rounded-lg border bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                      <div className="text-sm text-green-800 dark:text-green-200">
                        <strong>{t('limits.info', 'Info')}:</strong> {t('limits.sportsLimitInfo', 'This limit will apply to all sports betting activities')}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer - Action Buttons */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 shrink-0 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={handleSaveLimit}
                disabled={
                  isSavingLimit ||
                  !limitForm.type || 
                  (limitForm.type === 'casino' && limitForm.gameIds.length === 0 && limitForm.providerIds.length === 0) ||
                  !limitForm.currency || 
                  limitForm.maxBet === ''
                }
                className="flex-1 px-4 py-3 sm:py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600 transition-colors inline-flex items-center justify-center gap-2 font-medium shadow-sm touch-manipulation"
              >
                {isSavingLimit ? (
                  <>
                    <ArrowPathIcon className="w-5 h-5 animate-spin" />
                    {t('common.saving', 'Saving...')}
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5" />
                    {editingLimit ? t('common.update', 'Update') : t('common.save', 'Save')}
                  </>
                )}
              </button>
              <button
                onClick={handleCloseModal}
                disabled={isSavingLimit}
                className="flex-1 px-4 py-3 sm:py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 active:bg-gray-400 dark:active:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium touch-manipulation"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Provider Selection Modal */}
      {showProviderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-purple-50 dark:bg-purple-900/20">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('limits.selectProviders', 'Select Providers')}
              </h3>
              <button
                onClick={() => {
                  setShowProviderModal(false);
                  setTempProviderSelection([]);
                }}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {/* All Providers Option */}
                <button
                  type="button"
                  onClick={() => {
                    setTempProviderSelection(
                      tempProviderSelection.includes('all-providers')
                        ? []
                        : ['all-providers']
                    );
                  }}
                  className="w-full text-left p-3 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 ${
                      tempProviderSelection.includes('all-providers')
                        ? 'bg-red-600 border-red-600' 
                        : 'border-red-400 dark:border-red-600'
                    } flex items-center justify-center`}>
                      {tempProviderSelection.includes('all-providers') && (
                        <CheckIcon className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                        <ExclamationCircleIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-red-700 dark:text-red-300">
                          {t('limits.allGamesProviders', 'ALL GAMES/PROVIDERS')}
                        </span>
                  </div>
                  <span className="text-xs text-red-600 dark:text-red-400 font-semibold">
                    {t('limits.universal', 'UNIVERSAL')}
                  </span>
                </button>
                
                {/* Individual Providers */}
                {GAME_PROVIDERS.filter(p => p.id !== 'all').map(provider => (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => {
                      if (tempProviderSelection.includes('all-providers')) return;
                      setTempProviderSelection(prev =>
                        prev.includes(provider.id)
                          ? prev.filter(id => id !== provider.id)
                          : [...prev, provider.id]
                      );
                    }}
                    disabled={tempProviderSelection.includes('all-providers')}
                    className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition-colors ${
                      tempProviderSelection.includes('all-providers')
                        ? 'opacity-50 cursor-not-allowed bg-gray-50 dark:bg-gray-800/50'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 ${
                      tempProviderSelection.includes(provider.id)
                        ? 'bg-purple-600 border-purple-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    } flex items-center justify-center`}>
                      {tempProviderSelection.includes(provider.id) && (
                        <CheckIcon className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                    <span className="text-gray-900 dark:text-gray-100">{provider.name}</span>
                  </button>
                ))}
                
                {tempProviderSelection.includes('all-providers') && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p className="text-sm">{t('limits.deselectAllProviders', 'Deselect to choose individual providers')}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={() => {
                  setLimitForm(prev => ({ ...prev, providerIds: tempProviderSelection }));
                  setShowProviderModal(false);
                  setTempProviderSelection([]);
                }}
                disabled={tempProviderSelection.length === 0}
                className="flex-1 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {t('common.ok', 'OK')} {tempProviderSelection.length > 0 && `(${tempProviderSelection.length})`}
              </button>
              <button
                onClick={() => {
                  setShowProviderModal(false);
                  setTempProviderSelection([]);
                }}
                className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Game Selection Modal */}
      {showGameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[10000] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-blue-50 dark:bg-blue-900/20">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('limits.selectGames', 'Select Games')}
              </h3>
              <button
                onClick={() => {
                  setShowGameModal(false);
                  setTempGameSelection([]);
                  setGameSearchTerm('');
                }}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            {/* Search Bar */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <input
                type="text"
                value={gameSearchTerm}
                onChange={(e) => setGameSearchTerm(e.target.value)}
                placeholder={t('limits.searchGames', 'Search games...')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                autoFocus
              />
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {/* Add Custom Game Button */}
              <button
                type="button"
                onClick={() => setShowAddCustomGame(!showAddCustomGame)}
                className="w-full mb-4 p-3 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400 font-medium"
              >
                <PlusIcon className="w-5 h-5" />
                {t('limits.addCustomGame', 'Add Custom Game Type')}
              </button>
              
              {/* Custom Game Form */}
              {showAddCustomGame && (
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 space-y-3">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <CubeIcon className="w-4 h-4" />
                    {t('limits.createCustomGame', 'Create Custom Game Type')}
                  </h4>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('limits.gameName', 'Game Name')} *
                    </label>
                    <input
                      type="text"
                      value={customGameForm.name}
                      onChange={(e) => setCustomGameForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Baccarat, Poker, XXXtreme"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                    />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={customGameForm.isGeneral}
                        onChange={(e) => setCustomGameForm(prev => ({ ...prev, isGeneral: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {t('limits.allVariantsCheckbox', 'All Variants')}
                      </span>
                    </label>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('limits.allVariantsHelp', '(e.g., all Baccarat games)')}
                    </span>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('limits.icon', 'Icon')}
                    </label>
                    <select
                      value={customGameForm.icon}
                      onChange={(e) => setCustomGameForm(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                    >
                      <option value="🎰">🎰 Slot Machine</option>
                      <option value="🎲">🎲 Dice</option>
                      <option value="♠️">♠️ Spades</option>
                      <option value="♥️">♥️ Hearts</option>
                      <option value="♦️">♦️ Diamonds</option>
                      <option value="♣️">♣️ Clubs</option>
                      <option value="🃏">🃏 Joker</option>
                      <option value="🎯">🎯 Target</option>
                      <option value="💎">💎 Diamond</option>
                      <option value="🎪">🎪 Circus</option>
                      <option value="🎭">🎭 Theater</option>
                      <option value="🔮">🔮 Crystal Ball</option>
                    </select>
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        if (!customGameForm.name.trim()) {
                          showNotification('warning', t('common.warning'), t('limits.enterGameName', 'Please enter a game name'));
                          return;
                        }
                        
                        const newGame = {
                          id: customGameForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
                          name: customGameForm.name,
                          isGeneral: customGameForm.isGeneral,
                          icon: customGameForm.icon,
                          custom: true
                        };
                        
                        const updatedCustomGames = [...customCasinoGames, newGame];
                        setCustomCasinoGames(updatedCustomGames);
                        localStorage.setItem('customCasinoGames', JSON.stringify(updatedCustomGames));
                        
                        showNotification('success', t('common.success'), t('limits.customGameAdded', 'Custom game type added successfully'));
                        setCustomGameForm({ id: '', name: '', isGeneral: true, icon: '🎰' });
                        setShowAddCustomGame(false);
                      }}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                    >
                      {t('common.add', 'Add')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddCustomGame(false);
                        setCustomGameForm({ id: '', name: '', isGeneral: true, icon: '🎰' });
                      }}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                {/* Show custom games first if any */}
                {customCasinoGames.length > 0 && (
                  <>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                        {t('limits.customGames', 'Custom Games')}
                      </h4>
                      <button
                        type="button"
                        onClick={async () => {
                          const confirmed = await confirm({
                            title: t('limits.clearAll', 'Clear All'),
                            message: t('limits.confirmClearCustomGames', 'Clear all custom games?'),
                            confirmLabel: t('limits.clearAll', 'Clear All'),
                            cancelLabel: t('common.cancel', 'Cancel'),
                            tone: 'danger'
                          });
                          if (!confirmed) return;
                          setCustomCasinoGames([]);
                          localStorage.removeItem('customCasinoGames');
                        }}
                        className="text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                      >
                        {t('limits.clearAll', 'Clear All')}
                      </button>
                    </div>
                    {customCasinoGames.map(game => (
                      <button
                        key={game.id}
                        type="button"
                        onClick={() => {
                          setTempGameSelection(prev =>
                            prev.includes(game.id)
                              ? prev.filter(id => id !== game.id)
                              : [...prev, game.id]
                          );
                        }}
                        className={`w-full text-left p-3 rounded-lg flex items-center justify-between transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          tempGameSelection.includes('all-games') ? 'opacity-50' : ''
                        }`}
                        disabled={tempGameSelection.includes('all-games')}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 ${
                            tempGameSelection.includes(game.id)
                              ? 'bg-blue-600 border-blue-600' 
                              : 'border-gray-300 dark:border-gray-600'
                          } flex items-center justify-center`}>
                            {tempGameSelection.includes(game.id) && (
                              <CheckIcon className="w-3.5 h-3.5 text-white" />
                            )}
                          </div>
                          <span className="text-gray-900 dark:text-gray-100">{game.icon} {game.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {game.isGeneral && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">
                              {t('limits.allVariants', 'All Variants')}
                            </span>
                          )}
                          <span className="text-xs text-blue-600 dark:text-blue-400">
                            {t('limits.custom', 'Custom')}
                          </span>
                        </div>
                      </button>
                    ))}
                    <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                  </>
                )}
                
                {/* All Games Option */}
                <button
                  type="button"
                  onClick={() => {
                    setTempGameSelection(
                      tempGameSelection.includes('all-games')
                        ? []
                        : ['all-games']
                    );
                  }}
                  className="w-full text-left p-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 ${
                      tempGameSelection.includes('all-games')
                        ? 'bg-blue-600 border-blue-600' 
                        : 'border-blue-400 dark:border-blue-600'
                    } flex items-center justify-center`}>
                      {tempGameSelection.includes('all-games') && (
                        <CheckIcon className="w-3.5 h-3.5 text-white" />
                      )}
                    </div>
                        <ExclamationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        <span className="font-medium text-blue-700 dark:text-blue-300">
                          {t('limits.allGames', 'ALL GAMES')}
                        </span>
                  </div>
                  <span className="text-xs text-blue-600 dark:text-blue-400 font-semibold">
                    {t('limits.universal', 'UNIVERSAL')}
                  </span>
                </button>
                
                {/* Individual Games */}
                                {!tempGameSelection.includes('all-games') && filteredGames.map(game => (
                                  <button
                                    key={game.id}
                                    type="button"
                                    onClick={() => {
                                      setTempGameSelection(prev =>
                                        prev.includes(game.id)
                                          ? prev.filter(id => id !== game.id)
                                          : [...prev, game.id]
                                      );
                                    }}
                                    className={`w-full text-left p-3 rounded-lg flex items-center justify-between transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 ${
                                      game.isGeneral ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''
                                    }`}
                                  >
                                    <div className="flex items-center gap-3 flex-1 min-w-0">
                                      <div className={`w-5 h-5 rounded border-2 shrink-0 ${
                                        tempGameSelection.includes(game.id)
                                          ? 'bg-blue-600 border-blue-600' 
                                          : 'border-gray-300 dark:border-gray-600'
                                      } flex items-center justify-center`}>
                                        {tempGameSelection.includes(game.id) && (
                                          <CheckIcon className="w-3.5 h-3.5 text-white" />
                                        )}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="text-gray-900 dark:text-gray-100 truncate">
                                          {game.icon && <span className="mr-1">{game.icon}</span>}
                                          {game.name}
                                        </div>
                                        {game.id !== game.name && !game.isGeneral && (
                                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                            ID: {game.id}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {game.isGeneral && (
                                        <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded">
                                          {t('limits.allVariants', 'All Variants')}
                                        </span>
                                      )}
                                      {game.custom && (
                                        <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded">
                                          {t('limits.custom', 'Custom')}
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                ))}
                
                {tempGameSelection.includes('all-games') && (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <p className="text-sm">{t('limits.deselectAllGames', 'Deselect to choose individual games')}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={() => {
                  setLimitForm(prev => ({ ...prev, gameIds: tempGameSelection }));
                  setShowGameModal(false);
                  setTempGameSelection([]);
                  setGameSearchTerm('');
                }}
                disabled={tempGameSelection.length === 0}
                className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {t('common.ok', 'OK')} {tempGameSelection.length > 0 && `(${tempGameSelection.length})`}
              </button>
              <button
                onClick={() => {
                  setShowGameModal(false);
                  setTempGameSelection([]);
                  setGameSearchTerm('');
                }}
                className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Sports Event Selection Modal */}
      {showSportsEventModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[10000] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-lg shadow-2xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-green-50 dark:bg-green-900/20">
              {/* Mobile drag indicator */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full sm:hidden"></div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mt-2 sm:mt-0">
                {t('limits.selectSportsEvent', 'Select Sports Event')}
              </h3>
              <button
                onClick={() => {
                  setShowSportsEventModal(false);
                  setSportsEventSearchTerm('');
                }}
                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            
            {/* Search Bar */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 space-y-2">
              <input
                type="text"
                value={sportsEventSearchTerm}
                onChange={(e) => setSportsEventSearchTerm(e.target.value)}
                placeholder={t('limits.searchSportsEvents', 'Search events, leagues, categories...')}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                autoFocus
              />
              {!editingLimit && !tempSportsSelection.includes('all-sports') && visibleSportsEvents.length > 1 && (
                <button
                  type="button"
                  onClick={toggleAllShownSports}
                  className="w-full text-left px-1 py-1.5 rounded-lg flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  {(() => {
                    const selectedCount = visibleSportsEvents.filter((event) => tempSportsSelection.includes(event.id)).length;
                    const allSelected = selectedCount === visibleSportsEvents.length;
                    const someSelected = selectedCount > 0 && !allSelected;
                    return (
                      <>
                        <div className={`w-5 h-5 rounded border-2 shrink-0 flex items-center justify-center ${
                          allSelected || someSelected
                            ? 'bg-green-600 border-green-600'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {allSelected && <CheckIcon className="w-3.5 h-3.5 text-white" />}
                          {someSelected && <span className="block w-2.5 h-0.5 bg-white rounded-full" />}
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {allSelected
                            ? t('limits.deselectAll', 'Deselect All')
                            : t('limits.selectAll', 'Select All')}
                        </span>
                        <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                          {fillCount(t('limits.sportsMatchCount', { count: visibleSportsEvents.length }), visibleSportsEvents.length)}
                        </span>
                      </>
                    );
                  })()}
                </button>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {/* Add Custom Event Button */}
              <button
                type="button"
                onClick={() => setShowAddCustomEvent(!showAddCustomEvent)}
                className="w-full mb-4 p-3 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-center justify-center gap-2 text-blue-600 dark:text-blue-400 font-medium"
              >
                <PlusIcon className="w-5 h-5" />
                {t('limits.addCustomEvent', 'Add Custom Event/Keyword')}
              </button>
              
              {/* Custom Event Form */}
              {showAddCustomEvent && (
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 space-y-3">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <StarIcon className="w-4 h-4" />
                    {t('limits.createCustomEvent', 'Create Custom Event')}
                  </h4>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('limits.eventName', 'Event Name')} *
                    </label>
                    <input
                      type="text"
                      value={customEventForm.name}
                      onChange={(e) => setCustomEventForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Qatar Stars League, Winter Olympics"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('limits.category', 'Category')} *
                      </label>
                      <select
                        value={customEventForm.category}
                        onChange={(e) => setCustomEventForm(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                      >
                        <option value="event">{t('limits.eventCategory', 'Event')}</option>
                        <option value="league">{t('limits.leagueCategory', 'League')}</option>
                        <option value="regional">{t('limits.regionalCategory', 'Regional')}</option>
                        <option value="special">{t('limits.specialCategory', 'Special')}</option>
                        <option value="market">{t('limits.marketCategory', 'Market')}</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('limits.icon', 'Icon')}
                      </label>
                      <select
                        value={customEventForm.icon}
                        onChange={(e) => setCustomEventForm(prev => ({ ...prev, icon: e.target.value }))}
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                      >
                        {SPORTS_ICONS.map(icon => (
                          <option key={icon.value} value={icon.value}>
                            {icon.value} {icon.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('limits.eventAliases', 'Also called (comma separated)')}
                    </label>
                    <input
                      type="text"
                      value={customEventForm.aliases}
                      onChange={(e) => setCustomEventForm(prev => ({ ...prev, aliases: e.target.value }))}
                      placeholder="e.g., qatar stars, el dawre el qatare"
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800"
                    />
                    <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                      {t('limits.eventAliasesHint', 'The other ways people say it. This is what lets the AI assistant find the league when you ask it by voice.')}
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={isSavingEvent}
                      onClick={async () => {
                        if (!customEventForm.name.trim()) {
                          showNotification('warning', t('common.warning'), t('limits.enterEventName', 'Please enter an event name'));
                          return;
                        }

                        setIsSavingEvent(true);
                        const result = await createCustomSportsEvent({
                          name: customEventForm.name.trim(),
                          category: customEventForm.category,
                          icon: customEventForm.icon,
                          aliases: customEventForm.aliases
                            .split(',')
                            .map(alias => alias.trim())
                            .filter(Boolean)
                        });
                        setIsSavingEvent(false);

                        if (!result.success) {
                          showNotification('error', t('common.error'), result.error || t('limits.customEventFailed', 'Could not save the custom event'));
                          return;
                        }

                        setCustomSportsEvents(result.events);
                        showNotification('success', t('common.success'), t('limits.customEventAdded', 'Custom event added successfully'));
                        setCustomEventForm({ id: '', name: '', category: 'event', icon: '🏆', aliases: '' });
                        setShowAddCustomEvent(false);
                      }}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {isSavingEvent ? t('common.saving', 'Saving...') : t('common.add', 'Add')}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddCustomEvent(false);
                        setCustomEventForm({ id: '', name: '', category: 'event', icon: '🏆', aliases: '' });
                      }}
                      className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      {t('common.cancel', 'Cancel')}
                    </button>
                  </div>
                </div>
              )}
              
              <div className="space-y-3">
                {/* Show custom events first */}
                {!tempSportsSelection.includes('all-sports') && customSportsEvents.some((event) => sportsEventMatchesQuery(event, sportsEventSearchTerm)) && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                        {t('limits.customEvents', 'Custom Events')}
                      </h4>
                      <span className="text-[11px] text-gray-500 dark:text-gray-400">
                        {t('limits.sharedWithAllAdmins', 'Shared with all admins')}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {customSportsEvents
                        .filter((event) => sportsEventMatchesQuery(event, sportsEventSearchTerm))
                        .map(event => (
                          <div
                            key={event.id}
                            className={`w-full p-3 rounded-lg flex items-center gap-3 transition-colors ${
                              tempSportsSelection.includes(event.id)
                                ? 'bg-blue-100 dark:bg-blue-900/30 border-2 border-blue-500'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-transparent'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => toggleSportsEvent(event.id)}
                              className="flex-1 min-w-0 text-left flex items-center gap-3"
                            >
                              <div className={`w-5 h-5 rounded border-2 shrink-0 ${
                                tempSportsSelection.includes(event.id)
                                  ? 'bg-blue-600 border-blue-600'
                                  : 'border-gray-300 dark:border-gray-600'
                              } flex items-center justify-center`}>
                                {tempSportsSelection.includes(event.id) && (
                                  <CheckIcon className="w-3.5 h-3.5 text-white" />
                                )}
                              </div>
                              <span className="text-2xl">{event.icon}</span>
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {event.name}
                                </div>
                                <div className="text-xs text-blue-600 dark:text-blue-400 truncate">
                                  {(event.aliases || []).length > 1
                                    ? event.aliases.filter(alias => alias !== event.name).join(' · ')
                                    : t('limits.customKeyword', 'Custom')}
                                </div>
                              </div>
                            </button>
                            <button
                              type="button"
                              onClick={async () => {
                                const confirmed = await confirm({
                                  title: t('common.delete', 'Delete'),
                                  message: t('limits.confirmDeleteCustomEvent', `Remove "${event.name}" from the catalog? Limits already set on it are kept.`),
                                  confirmLabel: t('common.delete', 'Delete'),
                                  cancelLabel: t('common.cancel', 'Cancel'),
                                  tone: 'danger'
                                });
                                if (!confirmed) return;
                                const result = await deleteCustomSportsEvent(event.id);
                                if (result.success) {
                                  setCustomSportsEvents(result.events);
                                } else {
                                  showNotification('error', t('common.error'), result.error || t('limits.deleteEventFailed', 'Could not remove the event'));
                                }
                              }}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 shrink-0"
                              title={t('common.delete', 'Delete')}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
                
                {/* Group by category */}
                {['universal', 'event', 'league', 'regional', 'special', 'market'].map(category => {
                  if (tempSportsSelection.includes('all-sports') && category !== 'universal') return null;
                  const allEvents = getAllSportsEvents(customSportsEvents);
                  const events = allEvents.filter(e => 
                    e.category === category &&
                    !e.custom && // Don't show custom events here (shown above)
                    sportsEventMatchesQuery(e, sportsEventSearchTerm)
                  );
                  
                  if (events.length === 0) return null;
                  
                  return (
                    <div key={category}>
                      <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        {t(`limits.${category}Category`, category)}
                      </h4>
                      <div className="space-y-2">
                        {events.map(event => {
                          return (
                            <button
                              key={event.id}
                              type="button"
                              onClick={() => toggleSportsEvent(event.id)}
                              className={`w-full text-left p-3 rounded-lg flex items-center gap-3 transition-colors ${
                                tempSportsSelection.includes(event.id)
                                  ? 'bg-green-100 dark:bg-green-900/30 border-2 border-green-500'
                                  : 'hover:bg-gray-100 dark:hover:bg-gray-700 border-2 border-transparent'
                              }`}
                            >
                              <div className={`w-5 h-5 rounded border-2 shrink-0 ${
                                tempSportsSelection.includes(event.id)
                                  ? 'bg-green-600 border-green-600'
                                  : 'border-gray-300 dark:border-gray-600'
                              } flex items-center justify-center`}>
                                {tempSportsSelection.includes(event.id) && (
                                  <CheckIcon className="w-3.5 h-3.5 text-white" />
                                )}
                              </div>
                              {event.icon ? (
                                <span className="text-2xl">{event.icon}</span>
                              ) : (
                                <div className="p-2 rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                                  <TrophyIcon className="w-5 h-5" />
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                  {event.name}
                                </div>
                                {event.id === 'all-sports' && (
                                  <div className="text-xs text-red-600 dark:text-red-400 font-medium">
                                    {t('limits.appliestoAllSports', 'Applies to ALL sports')}
                                  </div>
                                )}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
                {tempSportsSelection.includes('all-sports') && (
                  <button
                    type="button"
                    onClick={() => toggleSportsEvent('all-sports')}
                    className="w-full text-center text-sm text-gray-500 dark:text-gray-400 py-4 hover:text-gray-700 dark:hover:text-gray-200"
                  >
                    {t('limits.deselectAllSports', 'Deselect All Sports to choose individual leagues')}
                  </button>
                )}
                {sportsEventSearchTerm.trim()
                  && !getAllSportsEvents(customSportsEvents).some((event) => sportsEventMatchesQuery(event, sportsEventSearchTerm))
                  && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                    {t('limits.noEventsFound', 'No matching events')}
                  </p>
                )}
              </div>
            </div>
            
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 bg-gray-50 dark:bg-gray-900/50">
              <button
                onClick={() => {
                  setLimitForm((prev) => ({ ...prev, sportsEventIds: tempSportsSelection }));
                  setShowSportsEventModal(false);
                  setSportsEventSearchTerm('');
                }}
                disabled={tempSportsSelection.length === 0}
                className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium touch-manipulation"
              >
                {t('common.ok', 'OK')} {tempSportsSelection.length > 0 && `(${tempSportsSelection.length})`}
              </button>
              <button
                onClick={() => {
                  setShowSportsEventModal(false);
                  setSportsEventSearchTerm('');
                }}
                className="flex-1 px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 active:bg-gray-400 dark:active:bg-gray-500 transition-colors font-medium touch-manipulation"
              >
                {t('common.cancel', 'Cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Limits;
